<template>
  <div class="pa-4">
    <v-row dense class="mb-2" align="center">
      <v-col>
        <h3>Atlas Guided Workflows <help-tip k="wf.status" /></h3>
      </v-col>
      <v-col cols="auto">
        <v-btn small outlined class="mr-2" @click="loadAll" :loading="loading">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
        <v-btn small outlined class="mr-2" @click="openCopyDialog">
          <v-icon small left>mdi-content-copy</v-icon>Copy From Client
        </v-btn>
        <v-btn small color="primary" @click="newWorkflow">
          <v-icon small left>mdi-plus</v-icon>New Workflow
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="workflows"
      :loading="loading"
      dense
      :items-per-page="20"
      sort-by="name"
      class="elevation-0 wf-table"
      @click:row="editWorkflow"
    >
      <template #[`item.status`]="{ item }">
        <v-chip x-small :color="statusColour(item.status)" dark>{{ item.status }}</v-chip>
      </template>
      <template #[`item.mcp_visible`]="{ item }">
        <v-chip
          x-small
          :color="item.mcp_visible ? 'teal lighten-4' : 'grey lighten-2'"
          :title="item.mcp_visible
            ? 'Offered to MCP hosts (Claude etc.)'
            : 'Hidden from MCP hosts — portal chat only'"
        >
          {{ item.mcp_visible ? 'On' : 'Off' }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn icon x-small class="mr-1" title="Edit" @click.stop="editWorkflow(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          v-if="item.client_id !== null && item.status !== 'published'"
          icon x-small class="mr-1" title="Publish"
          @click.stop="setStatus(item, 'published')"
        >
          <v-icon small>mdi-publish</v-icon>
        </v-btn>
        <v-btn
          v-if="item.client_id !== null && item.status === 'published'"
          icon x-small class="mr-1" title="Unpublish (back to draft)"
          @click.stop="setStatus(item, 'draft')"
        >
          <v-icon small>mdi-publish-off</v-icon>
        </v-btn>
        <!-- Deactivate = soft retirement: hidden from list/start but the
             row (and its instance history) stays. The path for rows the
             Delete button refuses (instances reference them). Publish
             brings an inactive row back. -->
        <v-btn
          v-if="item.client_id !== null && item.status !== 'inactive'"
          icon x-small class="mr-1" title="Deactivate (hide from use, keep history)"
          @click.stop="setStatus(item, 'inactive')"
        >
          <v-icon small>mdi-archive-outline</v-icon>
        </v-btn>
        <v-btn
          v-if="item.client_id !== null"
          icon x-small title="Delete"
          :disabled="item.instance_count > 0"
          @click.stop="confirmDelete(item)"
        >
          <v-icon small>mdi-delete-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- ── Workflow builder ─────────────────────────────────────── -->
    <v-dialog v-model="editor.open" max-width="1150" persistent scrollable>
      <v-card>
        <!-- d-flex explicitly: the portal's theme un-flexes v-card__title,
             so a v-spacer never pushes the close button to the right
             (same pattern as cAdminSettings' dialog headers). -->
        <v-card-title class="text-subtitle-1 py-2 d-flex justify-space-between align-center flex-nowrap">
          <span>{{ editor.isNew ? 'New Workflow' : `Edit: ${editor.name || '(unnamed)'}` }}</span>
          <v-btn icon @click="editor.open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <!-- pt-3: outlined field labels float ABOVE the input's top border,
             and this is a scroll container — without top padding the first
             row's labels clip at its edge. -->
        <v-card-text class="pt-3" style="max-height: 78vh">
          <v-alert v-if="editor.fromGlobal" dense text type="info" class="mb-3">
            This is the global template — saving creates a client-specific override with the
            same key.
          </v-alert>

          <!-- Cross-client copy match report: how the source client's
               template attrs / hierarchy types / chains were remapped
               onto this client, and what needs a manual pick. -->
          <v-alert
            v-if="editor.copyReport && editor.copyReport.length"
            dense text
            :type="editor.copyReport.some((l) => l.level === 'warn') ? 'warning' : 'info'"
            class="mb-3"
          >
            <div class="font-weight-medium mb-1">
              Copied from {{ editor.copySource }} — review the matches below, then save as draft.
            </div>
            <div v-for="(line, i) in editor.copyReport" :key="i" class="text-caption">
              <v-icon x-small class="mr-1">
                {{ line.level === 'warn' ? 'mdi-alert-outline' : 'mdi-check' }}
              </v-icon>{{ line.text }}
            </div>
          </v-alert>

          <v-row dense>
            <!-- No Key field: the wire identity (workflow_key) is minted
                 automatically on first save and stays internal — users
                 know workflows by name. -->
            <v-col cols="5">
              <v-text-field v-model="editor.name" label="Name" dense outlined />
            </v-col>
            <v-col cols="7">
              <v-text-field
                v-model="editor.description"
                label="Description (helps Atlas decide when to offer it)"
                dense outlined
              >
                <template #append-outer><help-tip k="wf.description" /></template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Atlas's opening message on pages where this workflow is
               configured as the page's guided workflow (Meta Admin →
               atlas_context_workflow). Empty = the standard greeting. -->
          <v-row dense>
            <v-col cols="12">
              <v-textarea
                v-model="editor.introMessage"
                label="Intro message (Atlas's first message on pages that auto-start this workflow)"
                hint="Shown instead of the standard greeting when a page's atlas_context_workflow points at this workflow. Leave empty to keep the standard greeting."
                persistent-hint
                rows="2"
                auto-grow
                counter="2000"
                dense outlined
              />
            </v-col>
          </v-row>

          <!-- Surface visibility: MCP hosts (Claude etc.) only see and
               start this workflow while the switch is on. The portal
               chat is unaffected either way. -->
          <v-row dense align="center">
            <v-col cols="4">
              <v-switch
                v-model="editor.mcpVisible"
                label="Offer via MCP (Claude and other MCP hosts)"
                dense hide-details class="mt-0"
              />
            </v-col>
            <v-col cols="8" class="text-caption grey--text align-self-center">
              When off, MCP hosts can't list or start this workflow — it stays available in the portal chat.
            </v-col>
          </v-row>

          <!-- Workflow-level feedback score widget: shown next to the
               Exit workflow link on live cards. -->
          <v-row dense align="center">
            <v-col cols="3" class="d-flex align-center">
              <v-switch
                v-model="editor.feedback.enabled"
                label="Feedback score (next to Exit workflow)"
                dense hide-details class="mt-0"
              />
              <help-tip k="wf.feedback" />
            </v-col>
            <template v-if="editor.feedback.enabled">
              <v-col cols="4">
                <v-text-field v-model="editor.feedback.label" label="Question" dense outlined hide-details />
              </v-col>
              <v-col cols="1">
                <v-select
                  v-model="editor.feedback.count"
                  :items="[2, 3, 4, 5, 6, 7, 8, 9, 10]"
                  label="Values"
                  dense outlined hide-details
                />
              </v-col>
              <v-col cols="2">
                <v-text-field v-model="editor.feedback.pre" label="Low-end label" dense outlined hide-details />
              </v-col>
              <v-col cols="2">
                <v-text-field v-model="editor.feedback.post" label="High-end label" dense outlined hide-details />
              </v-col>
            </template>
          </v-row>
          <v-row v-if="editor.feedback.enabled" dense align="center" class="mb-1">
            <v-col cols="3">
              <v-select
                v-model="editor.feedback.triggerMax"
                :items="feedbackTriggerItems"
                label="Ask why at or below"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="9">
              <v-text-field
                v-if="editor.feedback.triggerMax > 0"
                v-model="editor.feedback.prompt"
                label="Follow-up question for low scores"
                placeholder="What could have been better?"
                dense outlined hide-details
              />
            </v-col>
          </v-row>

          <v-tabs v-model="editor.tab" class="mb-3" height="36">
            <v-tab>Steps</v-tab>
            <v-tab>Flow</v-tab>
            <v-tab>JSON preview</v-tab>
            <v-tab>History</v-tab>
            <v-tab>Stats</v-tab>
          </v-tabs>

          <!-- ── Steps builder ─────────────────────────────────── -->
          <div v-show="editor.tab === 0">
            <v-expansion-panels v-model="editor.openStep" accordion>
              <v-expansion-panel v-for="(step, si) in editor.steps" :key="step.uid">
                <v-expansion-panel-header class="py-1">
                  <div class="d-flex align-center" style="gap: 8px">
                    <v-icon small>{{ stepIcon(step.kind) }}</v-icon>
                    <strong>{{ stepLabelFor(step.step_key) }}</strong>
                    <span class="text-caption grey--text">{{ kindLabel(step.kind) }}</span>
                    <v-chip v-if="step.is_start" x-small color="success" dark>start</v-chip>
                    <v-chip v-if="step.isEntry" x-small color="indigo" dark>entry</v-chip>
                    <v-chip v-if="step.requiresAction" x-small outlined>
                      needs {{ step.requiresAction }}
                    </v-chip>
                  </div>
                  <template #actions>
                    <v-btn icon x-small title="Move up" :disabled="si === 0" @click.stop="moveStep(si, -1)">
                      <v-icon small>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn icon x-small title="Move down" :disabled="si === editor.steps.length - 1" @click.stop="moveStep(si, 1)">
                      <v-icon small>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon x-small title="Remove step" @click.stop="removeStep(si)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                    <v-icon>$expand</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- No Step key field: keys are auto-generated internal
                       wiring — steps are shown by their prompt/kind. -->
                  <v-row dense align="center">
                    <v-col cols="4">
                      <v-select
                        v-model="step.kind"
                        :items="stepKinds"
                        label="Step type"
                        dense outlined hide-details
                      >
                        <template #append-outer><help-tip k="step.kind" /></template>
                      </v-select>
                    </v-col>
                    <v-col cols="3" class="d-flex align-center">
                      <v-switch
                        :input-value="step.is_start"
                        label="Start step"
                        dense hide-details class="mt-0"
                        :disabled="step.kind === 'terminal'"
                        @change="setStart(step, $event)"
                      />
                      <help-tip k="step.start" />
                    </v-col>
                    <v-col cols="5">
                      <v-select
                        v-model="step.requiresAction"
                        :items="requiresOptions"
                        label="Permission needed to enter"
                        dense outlined hide-details
                        clearable
                      >
                        <template #append-outer><help-tip k="step.requires" /></template>
                      </v-select>
                    </v-col>
                  </v-row>
                  <!-- No separate "limit to template types" input: the
                       permission scope derives from the step's own
                       template context — a job_search's document types,
                       a template-driven form's chosen template. Other
                       steps check the permission across any template. -->

                  <!-- Entry point: Atlas can start the workflow parked
                       directly on this step when the user's request
                       matches the hint ("I want to upload a profile" →
                       the upload step). Not offered on terminal steps,
                       doc_views (they show an earlier step's selection)
                       or the start step (it IS the default entry). -->
                  <v-row
                    v-if="step.kind !== 'terminal' && step.kind !== 'doc_view' && !step.is_start"
                    dense align="center" class="mt-1"
                  >
                    <v-col cols="3">
                      <v-switch
                        v-model="step.isEntry"
                        label="Entry point"
                        dense hide-details class="mt-0"
                      />
                    </v-col>
                    <v-col cols="9">
                      <v-text-field
                        v-if="step.isEntry"
                        v-model="step.entryHint"
                        label="Entry intent hint (what the user says to land here)"
                        placeholder="e.g. The user wants to upload a job document"
                        dense outlined hide-details
                      />
                    </v-col>
                  </v-row>

                  <!-- Prompt (all interactive kinds) -->
                  <v-text-field
                    v-if="step.kind !== 'terminal'"
                    v-model="step.prompt"
                    label="Prompt shown on the card"
                    dense outlined class="mt-3"
                  >
                    <template #append-outer><help-tip k="step.prompt" /></template>
                  </v-text-field>

                  <!-- choice: options + links -->
                  <template v-if="step.kind === 'choice'">
                    <div class="text-caption font-weight-medium mb-1">Options</div>
                    <v-row v-for="(opt, oi) in step.options" :key="oi" dense align="center">
                      <v-col cols="3">
                        <v-text-field v-model="opt.label" label="Label" dense outlined hide-details />
                      </v-col>
                      <v-col cols="4">
                        <v-text-field v-model="opt.description" label="Description (optional)" dense outlined hide-details />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="opt.to"
                          :items="targetSteps(step)"
                          label="→ goes to step"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                      <v-col cols="1">
                        <v-btn icon x-small @click="step.options.splice(oi, 1)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn x-small text color="primary" class="mt-1" @click="addOption(step)">
                      <v-icon x-small left>mdi-plus</v-icon>Add option
                    </v-btn>
                  </template>

                  <!-- job_search: filters, doc types + link on selection -->
                  <template v-if="step.kind === 'job_search'">
                    <v-row dense>
                      <v-col cols="6">
                        <v-select
                          v-model="step.searchDocTypes"
                          :items="templates.map((t) => t.tmpl_name)"
                          label="Limit to document types (blank = All)"
                          multiple small-chips deletable-chips
                          dense outlined hide-details
                        >
                          <template #append-outer><help-tip k="search.document_types" /></template>
                        </v-select>
                      </v-col>
                      <v-col cols="3">
                        <v-select
                          v-model="step.searchResultsMode"
                          :items="[
                            { text: 'In this card', value: 'inline' },
                            { text: 'Pass to next step', value: 'next_step' },
                          ]"
                          label="Results"
                          dense outlined hide-details
                        >
                          <template #append-outer><help-tip k="search.results_mode" /></template>
                        </v-select>
                      </v-col>
                      <v-col cols="3">
                        <v-select
                          v-model="step.selectedTo"
                          :items="targetSteps(step)"
                          :label="step.searchResultsMode === 'next_step' ? 'Searched → step' : 'Selected → step'"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                    </v-row>
                    <div v-if="step.searchResultsMode === 'next_step'" class="text-caption grey--text mt-1">
                      Pressing Search hands the criteria to the next step — link it to a
                      Document list step with "From previous search" so the results display there.
                      If the search finds nothing, this search card is shown again automatically.
                    </div>
                    <div class="text-caption font-weight-medium mt-2 mb-1">
                      Extra search fields (optional — the same fields All Jobs searches on)
                      <help-tip k="search.fields" />
                    </div>
                    <v-row v-for="(sf, sfi) in step.searchFields" :key="sfi" dense align="center">
                      <v-col cols="4">
                        <v-select
                          v-model="sf.attr"
                          :items="searchFieldOptions"
                          label="Field"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col cols="2">
                        <v-text-field v-model="sf.label" label="Label override" dense outlined hide-details />
                      </v-col>
                      <v-col cols="2">
                        <v-select
                          v-model="sf.valuesMode"
                          :items="searchValueModes"
                          label="Values"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col cols="1">
                        <v-checkbox v-model="sf.required" label="Req" dense hide-details class="mt-0" />
                      </v-col>
                      <v-col cols="1">
                        <v-text-field v-model="sf.placeholder" label="Hint" dense outlined hide-details />
                      </v-col>
                      <v-col cols="2" class="d-flex align-center flex-nowrap">
                        <v-btn icon x-small title="Move up" :disabled="sfi === 0" @click="moveRow(step.searchFields, sfi, -1)">
                          <v-icon small>mdi-arrow-up</v-icon>
                        </v-btn>
                        <v-btn icon x-small title="Move down" :disabled="sfi === step.searchFields.length - 1" @click="moveRow(step.searchFields, sfi, 1)">
                          <v-icon small>mdi-arrow-down</v-icon>
                        </v-btn>
                        <v-btn icon x-small title="Remove" @click="step.searchFields.splice(sfi, 1)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col v-if="sf.valuesMode === 'static'" cols="9" class="pl-6">
                        <v-text-field
                          v-model="sf.optionsText"
                          label="Allowed values (comma-separated — renders a dropdown)"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col v-if="sf.valuesMode === 'static'" cols="3">
                        <v-checkbox
                          v-model="sf.pills"
                          label="Pills (max 5 values)"
                          dense hide-details class="mt-0"
                        />
                      </v-col>
                      <template v-if="sf.valuesMode === 'template'">
                        <v-col cols="4" class="pl-6">
                          <v-select
                            v-model="sf.vfTemplate"
                            :items="templates.map((t) => t.tmpl_name)"
                            label="Template"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="5">
                          <v-autocomplete
                            v-model="sf.vfTpaId"
                            :items="templateAttrItems(sf.vfTemplate)"
                            label="Attribute (lookup → dropdown, hierarchy → tree)"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-checkbox
                            v-model="sf.forceSelect"
                            label="Flatten tree to dropdown"
                            dense hide-details class="mt-0"
                          />
                          <v-checkbox
                            v-model="sf.pills"
                            label="Pills (max 5 values)"
                            dense hide-details class="mt-0"
                          />
                          <!-- Tree picker only: a typed value is searched on
                               as-is, even with no matching node. -->
                          <v-checkbox
                            v-if="!sf.forceSelect"
                            v-model="sf.allowTyping"
                            label="Tree — allow typing new values"
                            dense hide-details class="mt-0"
                          />
                        </v-col>
                      </template>
                      <template v-if="sf.valuesMode === 'hierarchy'">
                        <v-col cols="4" class="pl-6">
                          <v-select
                            v-model="sf.hvHtId"
                            :items="hierarchies.map((h) => ({ text: h.name, value: h.hierarchy_type_id }))"
                            label="Hierarchy"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="5">
                          <v-select
                            v-model="sf.hvLevel"
                            :items="hierarchyLevelItems(sf.hvHtId)"
                            label="Level (level 2+ renders a tree)"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="3">
                          <v-checkbox
                            v-model="sf.forceSelect"
                            label="Flatten tree to dropdown"
                            dense hide-details class="mt-0"
                          />
                          <v-checkbox
                            v-model="sf.pills"
                            label="Pills (max 5 values)"
                            dense hide-details class="mt-0"
                          />
                          <!-- Tree picker only: a typed value is searched on
                               as-is, even with no matching node. -->
                          <v-checkbox
                            v-if="!sf.forceSelect"
                            v-model="sf.allowTyping"
                            label="Tree — allow typing new values"
                            dense hide-details class="mt-0"
                          />
                        </v-col>
                      </template>
                    </v-row>
                    <v-btn x-small text color="primary" class="mt-1" @click="addSearchField(step)">
                      <v-icon x-small left>mdi-plus</v-icon>Add search field
                    </v-btn>
                    <v-row dense align="center" class="mt-1">
                      <v-col cols="5">
                        <v-text-field
                          v-model="step.placeholder"
                          label="Search box hint (e.g. 'e.g. Software Engineer')"
                          dense outlined hide-details
                          :disabled="step.searchHideKeyword && step.searchFields.length > 0"
                        />
                      </v-col>
                      <v-col cols="7">
                        <v-switch
                          v-if="step.searchFields.length"
                          v-model="step.searchHideKeyword"
                          label="Hide the generic keyword box (search with the fields above only)"
                          dense hide-details class="mt-0"
                        />
                      </v-col>
                    </v-row>
                  </template>

                  <!-- doc_list: canned query / previous search + link on selection -->
                  <template v-if="step.kind === 'doc_list'">
                    <v-row dense>
                      <v-col cols="3">
                        <v-select
                          v-model="step.docListMode"
                          :items="[
                            { text: 'Canned query', value: 'query' },
                            { text: 'From previous search', value: 'search' },
                          ]"
                          label="Documents"
                          dense outlined hide-details
                        >
                          <template #append-outer><help-tip k="doclist.source" /></template>
                        </v-select>
                      </v-col>
                      <template v-if="step.docListMode === 'query'">
                        <v-col cols="2">
                          <v-text-field
                            v-model="step.docListQuery"
                            label="Fixed keyword (optional)"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="2">
                          <v-combobox
                            v-model="step.docListTypes"
                            label="Doc types (blank = defaults)"
                            multiple small-chips deletable-chips dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="2">
                          <v-combobox
                            v-model="step.docListStatuses"
                            label="Statuses (blank = defaults)"
                            multiple small-chips deletable-chips dense outlined hide-details
                          />
                        </v-col>
                      </template>
                      <v-col v-if="step.docListMode === 'search'" cols="6">
                        <v-select
                          v-model="step.docListSearchStep"
                          :items="targetSteps(step)"
                          label="Search step (blank = the step just before)"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                      <v-col cols="1">
                        <v-text-field
                          v-model.number="step.docListLimit"
                          label="Rows"
                          type="number"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col cols="2">
                        <v-select
                          v-model="step.selectedTo"
                          :items="targetSteps(step)"
                          label="Selected → step"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                      <v-col cols="3">
                        <v-text-field
                          v-model="step.docListOpenLabel"
                          label="Open button label (portal; default 'Open')"
                          dense outlined hide-details
                        />
                      </v-col>
                    </v-row>
                    <div class="text-caption grey--text mt-1">
                      The list is resolved when the step is reached — only documents the user is
                      permitted to view are shown.
                    </div>
                  </template>

                  <!-- doc_view: source + link on confirm -->
                  <template v-if="step.kind === 'doc_view'">
                    <v-row dense>
                      <v-col cols="4">
                        <v-select
                          v-model="step.docViewSourceStep"
                          :items="targetSteps(step)"
                          label="Document from step (blank = previous step)"
                          dense outlined hide-details
                          clearable
                        >
                          <template #append-outer><help-tip k="docview.source_step" /></template>
                        </v-select>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          v-model="step.confirmLabel"
                          label="Confirm button label (default 'Continue')"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="step.confirmedTo"
                          :items="targetSteps(step)"
                          label="Confirmed → go to step (blank = ends here)"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                    </v-row>
                    <div class="text-caption grey--text mt-1">
                      Shows the document selected in a preceding search or list step, read-only,
                      with an "Open" action. Leave the confirmed link blank to make this the
                      end of the workflow — the Continue button is hidden and the flow completes
                      when the document is shown.
                    </div>
                  </template>

                  <!-- doc_upload: file → extract/chain → gap-fill → duplicate check → review → create -->
                  <template v-if="step.kind === 'doc_upload'">
                    <v-row dense>
                      <v-col cols="4">
                        <v-select
                          v-model="step.uploadTmplName"
                          :items="templates.map((t) => t.tmpl_name)"
                          label="Target template"
                          dense outlined hide-details
                        >
                          <template #append-outer><help-tip k="upload.template" /></template>
                        </v-select>
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="step.uploadMode"
                          :items="[
                            { text: 'Extract with Atlas (simple, no chain)', value: 'extract' },
                            { text: 'Extract with a prompt chain', value: 'chain' },
                          ]"
                          label="Extraction"
                          dense outlined hide-details
                        >
                          <template #append-outer><help-tip k="upload.extraction_mode" /></template>
                        </v-select>
                      </v-col>
                    </v-row>
                    <v-row v-if="step.uploadMode === 'chain'" dense>
                      <v-col cols="4">
                        <v-select
                          v-model="step.uploadChainId"
                          :items="templateChainItems(step.uploadTmplName)"
                          label="Content chain (linked to this template)"
                          no-data-text="No prompt chains are linked to this template"
                          dense outlined hide-details
                          @change="step.uploadDocumentInput = null"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="step.uploadDocumentInput"
                          :items="chainInputItems(step.uploadChainId)"
                          label="Chain input receiving the document text"
                          no-data-text="Pick a chain first"
                          dense outlined hide-details
                        />
                      </v-col>
                    </v-row>
                    <!-- Chain-free extraction: the sections to pull from
                         the document (the template's capturable
                         attributes and placement hints are extracted
                         automatically). Same rows as the create step's
                         drafted sections. -->
                    <template v-if="step.uploadMode === 'extract'">
                      <div class="text-caption font-weight-medium mt-2 mb-1">
                        Sections to extract from the document (instructions steer the extraction;
                        attributes and placements are picked up automatically)
                        <help-tip k="upload.sections" />
                        <help-guide-dialog guide="extraction" link-text="When to write guidance" class="ml-2" />
                      </div>
                      <v-row v-for="(gp, gi) in step.uploadExtractParts" :key="`uext-${gi}`" dense align="center">
                        <v-col cols="3">
                          <v-select
                            v-model="gp.tp_id"
                            :items="templatePartItems(step.uploadTmplName)"
                            :label="gp.part && !gp.tp_id ? `Section (by name: '${gp.part}')` : 'Section'"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="5">
                          <v-textarea
                            v-model="gp.instructions"
                            label="Extraction guidance for this section (optional)"
                            rows="2" auto-grow dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="2">
                          <v-select v-model="gp.format" :items="generateFormatOptions" label="Shape" dense outlined hide-details />
                        </v-col>
                        <v-col cols="2" class="d-flex align-center flex-nowrap">
                          <v-text-field
                            v-model.number="gp.minItems"
                            label="Min" type="number" dense outlined hide-details class="mr-1"
                            :disabled="gp.format === 'text'"
                          />
                          <v-text-field
                            v-model.number="gp.maxItems"
                            label="Max" type="number" dense outlined hide-details class="mr-1"
                            :disabled="gp.format === 'text'"
                          />
                          <v-btn icon x-small title="Remove" @click="step.uploadExtractParts.splice(gi, 1)">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-btn x-small text color="primary" class="mb-2" @click="addUploadExtractPart(step)">
                        <v-icon x-small left>mdi-plus</v-icon>Add section
                      </v-btn>
                      <v-textarea
                        v-model="step.uploadExtractContext"
                        label="Extraction context (document conventions, terminology — optional)"
                        rows="2" auto-grow dense outlined hide-details class="mb-1"
                      >
                        <template #append-outer><help-tip k="upload.context" /></template>
                      </v-textarea>
                    </template>
                    <v-row dense>
                      <v-col cols="4">
                        <v-text-field
                          v-model="step.uploadAccept"
                          label="Accepted types (comma-separated; blank = pdf, docx, txt)"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          v-model="step.uploadConfirmLabel"
                          label="Create button label (default 'Create document')"
                          dense outlined hide-details
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="step.createdTo"
                          :items="targetSteps(step)"
                          label="Created → go to step (blank = ends here)"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                    </v-row>
                    <div class="text-caption grey--text mt-1">
                      The user drops or picks a file; Atlas (or the chosen chain) drafts the
                      document from it; missing mandatory attributes are prompted for; a
                      duplicate check runs on the identified position; the draft and the
                      original file are confirmed before the document is created (the original
                      is attached to it).
                    </div>
                  </template>

                  <!-- form: fields + link on submit -->
                  <template v-if="step.kind === 'form'">
                    <v-row dense>
                      <v-col cols="4">
                        <v-text-field v-model="step.submitLabel" label="Submit button label" dense outlined hide-details />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="step.fieldsMode"
                          :items="[
                            { text: 'Fields from a template', value: 'template' },
                            { text: 'Custom fields', value: 'static' },
                          ]"
                          label="Field source"
                          dense outlined hide-details
                        >
                          <template #append-outer><help-tip k="form.field_source" /></template>
                        </v-select>
                      </v-col>
                      <v-col cols="4">
                        <!-- Create-on-submit forms are their own end point:
                             leaving this blank completes the workflow. -->
                        <v-select
                          v-model="step.submittedTo"
                          :items="targetSteps(step)"
                          :label="step.createMode !== 'none'
                            ? 'When submitted → go to step (blank = ends here)'
                            : 'When submitted → go to step'"
                          dense outlined hide-details clearable
                        />
                      </v-col>
                    </v-row>

                    <!-- template-derived fields -->
                    <template v-if="step.fieldsMode === 'template'">
                      <v-select
                        v-model="step.templateName"
                        :items="templates.map((t) => t.tmpl_name)"
                        label="Template"
                        dense outlined hide-details class="mt-2"
                        style="max-width: 400px"
                      />
                      <!-- On-submit create behaviour: run an authored content
                           chain (listed for THIS template), or create the
                           document directly from the captured fields. -->
                      <v-row dense align="center" class="mt-1">
                        <v-col cols="4">
                          <v-select
                            v-model="step.createMode"
                            :items="createModeOptions"
                            label="On submit"
                            dense outlined hide-details
                          >
                            <template #append-outer><help-tip k="form.on_submit" /></template>
                          </v-select>
                        </v-col>
                        <v-col v-if="step.createMode === 'chain' || step.createMode === 'user_choice'" cols="4">
                          <v-select
                            v-model="step.createChainId"
                            :items="templateChainItems(step.templateName)"
                            label="Content chain (linked to this template)"
                            no-data-text="No prompt chains are linked to this template"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col class="text-caption grey--text">
                          <template v-if="step.createMode === 'chain'">
                            The chain runs in the chat when the form is submitted
                            (progress inline), then the document is created with
                            the submitted values and opens for review.
                          </template>
                          <template v-else-if="step.createMode === 'direct'">
                            The document is created immediately with just the
                            captured fields, then opens for review.
                          </template>
                          <template v-else-if="step.createMode === 'user_choice'">
                            On submit the user is asked whether to generate the
                            content with the chain or create the document with
                            just the captured fields.
                          </template>
                          <template v-else-if="step.createMode === 'generate'">
                            Atlas drafts the sections below in one pass from the
                            captured details, then the document is created and
                            opens for review. No prompt chain involved.
                          </template>
                          <template v-else-if="step.createMode === 'generate_user_choice'">
                            On submit the user is asked whether Atlas should
                            draft the sections below or create the document with
                            just the captured fields.
                          </template>
                        </v-col>
                      </v-row>

                      <!-- Title triage: hold the submit while similar existing
                           titles are shown — direct name match first, else
                           AI-suggested similar roles from the catalogue. -->
                      <v-row v-if="step.createMode !== 'none'" dense align="center">
                        <v-col cols="12">
                          <v-checkbox
                            v-model="step.titleTriage"
                            label="Check for similar titles before creating (shows matching existing roles; if none match by name, Atlas suggests similar roles from the catalogue)"
                            dense hide-details class="mt-0"
                          />
                          <v-checkbox
                            v-model="step.createMissingNode"
                            label="Offer to add a brand-new title to the job hierarchy (user picks the parent; needs the client's hierarchy add permissions — others are told it can't be placed)"
                            dense hide-details class="mt-0"
                          />
                        </v-col>
                      </v-row>

                      <!-- UNIFIED field/section list: everything the template
                           offers, in template order — each row set to Input
                           (asked on the form), Output (drafted by Atlas, in
                           generate mode), or ignored. Labels, locked
                           parts/attributes and derived values are excluded or
                           shown inert. In chain mode, part rows the selected
                           chain writes are annotated read-only (the chain's
                           Document Formatter owns those). The view binds
                           straight onto templateAttrs / generateParts, so
                           save/load and validation are unchanged. -->
                      <div class="text-caption font-weight-medium mt-3 mb-1">
                        Template fields &amp; sections — what the form asks for (input)
                        <template v-if="isGenerateMode(step)"> and what Atlas drafts (output)</template>
                        <help-tip k="form.unified_list" />
                        <help-guide-dialog
                          v-if="isGenerateMode(step)"
                          guide="drafting"
                          link-text="How to write instructions"
                          class="ml-2"
                        />
                      </div>
                      <!-- Consistent grid: name (3) · role (2) · config
                           (label 2 / control-or-level 2 / placeholder 2 /
                           req 1). Grouped attr names indent INSIDE their
                           column so the role column always lines up. A row
                           whose value is hierarchy-derived AND whose
                           hierarchy is a captured input shows a pill
                           instead of a role select — nothing to configure. -->
                      <div v-for="row in unifiedRows(step)" :key="row.key">
                        <!-- part heading (tag parts + multi-attr parts) -->
                        <v-row v-if="row.kind === 'part'" dense align="center" class="mt-1">
                          <v-col cols="3" class="text-body-2 font-weight-medium">
                            {{ row.part.tp_name }}
                            <span class="text-caption grey--text font-weight-regular"> {{ partHint(row) }}</span>
                          </v-col>
                          <v-col cols="2">
                            <v-chip
                              v-if="derivedFromInput(step, row) && !row.gp"
                              x-small color="indigo lighten-4"
                              :title="`Filled from the captured ${htName(derivedFromInput(step, row))} placement — nothing to configure`"
                            >
                              Derived from input
                            </v-chip>
                            <v-select
                              v-else-if="row.part.has_tag && isGenerateMode(step)"
                              :value="tagChoice(step, row.part)"
                              :items="tagChoiceOptions"
                              label="Skills"
                              dense outlined hide-details
                              @change="setTagChoice(step, row.part, $event)"
                            />
                            <v-select
                              v-else-if="isGenerateMode(step) && !row.part.has_tag"
                              :value="row.gp ? 'output' : 'ignore'"
                              :items="[
                                { text: 'Ignored', value: 'ignore' },
                                { text: 'Draft with Atlas (output)', value: 'output' },
                              ]"
                              :disabled="row.inputAttrCount > 0"
                              label="Role"
                              dense outlined hide-details
                              @change="setPartRole(step, row, $event)"
                            />
                            <v-chip
                              v-else-if="chainWritesPart(step, row.part.tp_id)"
                              x-small color="teal lighten-4"
                              title="The selected chain's Document Formatter writes this part — configured in the chain builder, not here"
                            >
                              Written by the chain
                            </v-chip>
                          </v-col>
                          <v-col class="text-caption grey--text">
                            <template v-if="isGenerateMode(step) && !row.part.has_tag && row.inputAttrCount > 0">
                              captured fields below win over drafted content — this part can't also be an output
                            </template>
                          </v-col>
                        </v-row>
                        <!-- output config, under its part / merged row -->
                        <v-row
                          v-if="(row.kind === 'part' || row.kind === 'merged') && row.gp && isGenerateMode(step)"
                          dense align="center"
                        >
                          <v-col cols="3" />
                          <v-col cols="5">
                            <v-textarea
                              v-model="row.gp.instructions"
                              label="Instructions for this section"
                              rows="2" auto-grow dense outlined hide-details
                            />
                          </v-col>
                          <v-col cols="2">
                            <v-select v-model="row.gp.format" :items="generateFormatOptions" label="Shape" dense outlined hide-details />
                          </v-col>
                          <v-col cols="1">
                            <v-text-field v-model.number="row.gp.minItems" label="Min" type="number" dense outlined hide-details :disabled="row.gp.format === 'text'" />
                          </v-col>
                          <v-col cols="1">
                            <v-text-field v-model.number="row.gp.maxItems" label="Max" type="number" dense outlined hide-details :disabled="row.gp.format === 'text'" />
                          </v-col>
                        </v-row>
                        <!-- child-row bounds (Responsibility → Tasks): counts
                             are structure — schema-enforced, not prose. -->
                        <v-row
                          v-if="(row.kind === 'part' || row.kind === 'merged') && row.gp && isGenerateMode(step) && row.child"
                          dense align="center"
                        >
                          <v-col cols="3" />
                          <v-col cols="2">
                            <v-text-field
                              v-model.number="row.gp.childMin"
                              :label="`${row.child.tp_name} rows per item — min`"
                              type="number" dense outlined hide-details
                            />
                          </v-col>
                          <v-col cols="2">
                            <v-text-field
                              v-model.number="row.gp.childMax"
                              :label="`${row.child.tp_name} rows per item — max`"
                              type="number" dense outlined hide-details
                            />
                          </v-col>
                        </v-row>
                        <!-- merged part+attribute (single capturable attr):
                             one row, one role — input, output, or ignored -->
                        <v-row v-if="row.kind === 'merged'" dense align="center" class="mt-1">
                          <v-col cols="3" class="text-body-2 font-weight-medium">
                            {{ row.part.tp_name }}
                            <span class="text-caption grey--text font-weight-regular">
                              {{ attrHint(row.attr) || partHint(row) }}</span>
                          </v-col>
                          <v-col cols="2">
                            <v-chip
                              v-if="derivedFromInput(step, row) && !row.fa && !row.gp"
                              x-small color="indigo lighten-4"
                              :title="`Filled from the captured ${htName(derivedFromInput(step, row))} placement — nothing to configure`"
                            >
                              Derived from input
                            </v-chip>
                            <v-select
                              v-else
                              :value="mergedRole(row)"
                              :items="mergedRoleItems(step, row)"
                              label="Role"
                              dense outlined hide-details
                              @change="setMergedRole(step, row, $event)"
                            />
                          </v-col>
                          <template v-if="row.fa">
                            <v-col cols="2">
                              <v-text-field v-model="row.fa.label" label="Label override" dense outlined hide-details />
                            </v-col>
                            <v-col cols="2">
                              <v-select
                                v-model="row.fa.type"
                                :items="attrTypeOptions"
                                label="Control"
                                dense outlined hide-details clearable
                                placeholder="auto"
                              />
                            </v-col>
                            <v-col cols="2">
                              <v-text-field v-model="row.fa.placeholder" label="Placeholder" dense outlined hide-details />
                            </v-col>
                            <v-col cols="1">
                              <v-checkbox v-model="row.fa.required" label="Req" dense hide-details class="mt-0" />
                            </v-col>
                          </template>
                          <v-col v-else class="text-caption grey--text">
                            <v-chip
                              v-if="chainWritesPart(step, row.part.tp_id)"
                              x-small color="teal lighten-4"
                              title="The selected chain's Document Formatter writes this part — configured in the chain builder, not here"
                            >
                              Written by the chain
                            </v-chip>
                          </v-col>
                        </v-row>
                        <!-- capturable attribute (multi-attr parts) -->
                        <v-row v-else-if="row.kind === 'attr'" dense align="center">
                          <v-col cols="3" class="text-body-2 pl-6">
                            {{ row.attr.title }}
                            <span class="text-caption grey--text"> {{ attrHint(row.attr) }}</span>
                          </v-col>
                          <v-col cols="2">
                            <v-chip
                              v-if="derivedFromInput(step, row) && !row.fa"
                              x-small color="indigo lighten-4"
                              :title="`Filled from the captured ${htName(derivedFromInput(step, row))} placement — nothing to configure`"
                            >
                              Derived from input
                            </v-chip>
                            <v-select
                              v-else
                              :value="row.fa ? 'input' : 'ignore'"
                              :items="[
                                { text: 'Ignored', value: 'ignore' },
                                { text: 'Ask on the form (input)', value: 'input' },
                              ]"
                              label="Role"
                              dense outlined hide-details
                              @change="setAttrRole(step, row, $event)"
                            />
                          </v-col>
                          <template v-if="row.fa">
                            <v-col cols="2">
                              <v-text-field v-model="row.fa.label" label="Label override" dense outlined hide-details />
                            </v-col>
                            <v-col cols="2">
                              <v-select
                                v-model="row.fa.type"
                                :items="attrTypeOptions"
                                label="Control"
                                dense outlined hide-details clearable
                                placeholder="auto"
                              />
                            </v-col>
                            <v-col cols="2">
                              <v-text-field v-model="row.fa.placeholder" label="Placeholder" dense outlined hide-details />
                            </v-col>
                            <v-col cols="1">
                              <v-checkbox v-model="row.fa.required" label="Req" dense hide-details class="mt-0" />
                            </v-col>
                          </template>
                        </v-row>
                        <!-- derived value: written by the engine, never asked.
                             partName present = the whole part exists to show
                             the derived value (rendered unindented). -->
                        <v-row v-else-if="row.kind === 'derived'" dense align="center" :class="row.partName ? 'mt-1' : ''">
                          <v-col cols="3" class="text-body-2 grey--text" :class="row.partName ? '' : 'pl-6'">
                            {{ row.partName || row.attr.title }}
                          </v-col>
                          <v-col class="text-caption grey--text">
                            derived automatically from the captured level &amp; track
                          </v-col>
                        </v-row>
                        <!-- hierarchy placement picker -->
                        <v-row v-else-if="row.kind === 'hierarchy'" dense align="center">
                          <v-col cols="3" class="text-body-2 font-weight-medium">
                            {{ row.ht.name }}
                            <span class="text-caption grey--text font-weight-regular"> hierarchy</span>
                          </v-col>
                          <v-col cols="2">
                            <v-select
                              :value="row.fa ? 'input' : 'ignore'"
                              :items="[
                                { text: 'Ignored', value: 'ignore' },
                                { text: 'Ask on the form (input)', value: 'input' },
                              ]"
                              label="Role"
                              dense outlined hide-details
                              @change="setHierarchyRole(step, row, $event)"
                            />
                          </v-col>
                          <template v-if="row.fa">
                            <v-col cols="2">
                              <v-text-field v-model="row.fa.label" label="Label override" dense outlined hide-details />
                            </v-col>
                            <v-col cols="2">
                              <v-select
                                v-model="row.fa.htLevel"
                                :items="hierarchyLevelItems(row.ht.hierarchy_type_id)"
                                label="Selectable level"
                                dense outlined hide-details
                              />
                            </v-col>
                            <v-col cols="2" />
                            <v-col cols="1">
                              <v-checkbox v-model="row.fa.required" label="Req" dense hide-details class="mt-0" />
                            </v-col>
                          </template>
                        </v-row>
                        <!-- section divider -->
                        <div v-else-if="row.kind === 'section'" class="text-caption font-weight-medium mt-2 mb-1">
                          {{ row.label }}
                        </div>
                      </div>

                      <!-- Form field order: templateAttrs array order IS the
                           saved template_fields.attrs order, and the engine
                           renders the form's inputs in exactly that order.
                           The unified list above stays in template order for
                           configuration; the asked order is authored here. -->
                      <template v-if="step.templateAttrs.length > 1">
                        <div class="text-caption font-weight-medium mt-3 mb-1">
                          Form field order — the order the form asks its inputs
                          <help-tip k="form.field_order" />
                        </div>
                        <div
                          v-for="(fa, fi) in step.templateAttrs"
                          :key="`ord-${fa.tpa_id || fa.attr || fi}`"
                          class="d-flex align-center text-body-2"
                        >
                          <span class="text-caption grey--text mr-2" style="width: 18px; text-align: right">{{ fi + 1 }}.</span>
                          <span class="mr-2">{{ inputEntryLabel(step, fa) }}</span>
                          <v-btn icon x-small title="Move up" :disabled="fi === 0" @click="moveRow(step.templateAttrs, fi, -1)">
                            <v-icon small>mdi-arrow-up</v-icon>
                          </v-btn>
                          <v-btn icon x-small title="Move down" :disabled="fi === step.templateAttrs.length - 1" @click="moveRow(step.templateAttrs, fi, 1)">
                            <v-icon small>mdi-arrow-down</v-icon>
                          </v-btn>
                        </div>
                      </template>

                      <!-- Config entries the current template can't account
                           for (renamed/removed attrs, foreign-copy leftovers)
                           — visible so they can be removed, never silent. -->
                      <v-alert v-if="unresolvedEntries(step).length" dense text type="warning" class="mt-2">
                        <div class="font-weight-medium mb-1">
                          Configured entries that don't match this template:
                        </div>
                        <div
                          v-for="(u, ui) in unresolvedEntries(step)"
                          :key="ui"
                          class="text-caption d-flex align-center"
                        >
                          <span class="mr-2">{{ u.label }}</span>
                          <v-btn icon x-small title="Remove" @click="u.remove()">
                            <v-icon x-small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </v-alert>

                      <v-textarea
                        v-if="isGenerateMode(step)"
                        v-model="step.generateContext"
                        label="Drafting context (tone, house style, rules — applies to every drafted section)"
                        rows="2" auto-grow dense outlined hide-details class="mt-3"
                      >
                        <template #append-outer><help-tip k="form.context" /></template>
                      </v-textarea>
                    </template>

                    <!-- custom static fields -->
                    <template v-if="step.fieldsMode === 'static'">
                      <div class="text-caption font-weight-medium mt-2 mb-1">Fields</div>
                      <v-row v-for="(f, fi) in step.staticFields" :key="fi" dense align="center">
                        <v-col cols="3">
                          <v-text-field v-model="f.label" label="Label" dense outlined hide-details />
                        </v-col>
                        <v-col cols="2">
                          <v-select v-model="f.type" :items="staticTypeOptions" label="Control" dense outlined hide-details />
                        </v-col>
                        <v-col cols="3">
                          <v-text-field
                            v-if="isOptionControl(f.type)"
                            v-model="f.optionsText"
                            label="Options (comma-separated)"
                            dense outlined hide-details
                          />
                          <v-text-field
                            v-else
                            v-model="f.placeholder"
                            label="Placeholder"
                            dense outlined hide-details
                          />
                        </v-col>
                        <v-col cols="2">
                          <v-checkbox v-model="f.required" label="Required" dense hide-details class="mt-0" />
                        </v-col>
                        <v-col cols="2" class="d-flex align-center flex-nowrap">
                          <v-btn icon x-small title="Move up" :disabled="fi === 0" @click="moveRow(step.staticFields, fi, -1)">
                            <v-icon small>mdi-arrow-up</v-icon>
                          </v-btn>
                          <v-btn icon x-small title="Move down" :disabled="fi === step.staticFields.length - 1" @click="moveRow(step.staticFields, fi, 1)">
                            <v-icon small>mdi-arrow-down</v-icon>
                          </v-btn>
                          <v-btn icon x-small title="Remove" @click="step.staticFields.splice(fi, 1)">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-btn x-small text color="primary" class="mt-1" @click="addStaticField(step)">
                        <v-icon x-small left>mdi-plus</v-icon>Add field
                      </v-btn>
                    </template>
                  </template>

                  <!-- guidance (all kinds; the whole point of terminal steps) -->
                  <v-textarea
                    v-model="step.guidance"
                    :label="step.kind === 'terminal'
                      ? 'What should Atlas do when the workflow reaches this end point?'
                      : 'Guidance for Atlas while this step is showing (optional)'"
                    dense outlined rows="2" auto-grow class="mt-3"
                    hide-details
                  >
                    <template #append-outer><help-tip k="step.guidance" /></template>
                  </v-textarea>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div class="mt-3">
              <v-btn small outlined class="mr-2" @click="addStep('choice')">
                <v-icon small left>mdi-format-list-bulleted</v-icon>Add choice
              </v-btn>
              <v-btn small outlined class="mr-2" @click="addStep('job_search')">
                <v-icon small left>mdi-magnify</v-icon>Add job search
              </v-btn>
              <v-btn small outlined class="mr-2" @click="addStep('doc_list')">
                <v-icon small left>mdi-file-document-multiple-outline</v-icon>Add document list
              </v-btn>
              <v-btn small outlined class="mr-2" @click="addStep('doc_view')">
                <v-icon small left>mdi-file-document-outline</v-icon>Add document view
              </v-btn>
              <v-btn small outlined class="mr-2" @click="addStep('form')">
                <v-icon small left>mdi-form-select</v-icon>Add document create
              </v-btn>
              <v-btn small outlined @click="addStep('terminal')">
                <v-icon small left>mdi-flag-checkered</v-icon>Add end point
              </v-btn>
            </div>
          </div>

          <!-- ── Flow diagram ──────────────────────────────────── -->
          <div v-show="editor.tab === 1" class="wf-flow">
            <span class="wf-flow__help"><help-tip k="flow.editing" /></span>
            <!-- Frame-level Add: new step, unlinked (wire it via a box's
                 ⋯ menu or the Steps tab). -->
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn small text color="primary" class="wf-flow__add" v-bind="attrs" v-on="on">
                  <v-icon small left>mdi-plus</v-icon>Add step
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="k in stepKinds"
                  :key="k.value"
                  @click="addStepInFlow(k.value)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>{{ stepIcon(k.value) }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ k.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <div v-if="!editor.steps.length" class="text-caption grey--text pa-4">
              No steps yet — use "Add step" above.
            </div>
            <div v-else class="wf-flow__scroll">
              <svg :width="flowGraph.width" :height="flowGraph.height">
                <defs>
                  <marker id="wf-arrow" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto">
                    <path d="M0,0 L9,4.5 L0,9 z" fill="#94a3b8" />
                  </marker>
                </defs>
                <path
                  v-for="(e, i) in flowGraph.edges"
                  :key="`e${i}`"
                  :d="e.path"
                  fill="none"
                  stroke="#94a3b8"
                  stroke-width="1.5"
                  marker-end="url(#wf-arrow)"
                />
                <text
                  v-for="(e, i) in flowGraph.edges"
                  :key="`el${i}`"
                  :x="e.labelX"
                  :y="e.labelY"
                  class="wf-flow__edge-label"
                  text-anchor="middle"
                >{{ e.label }}</text>
                <g
                  v-for="n in flowGraph.nodes"
                  :key="n.key"
                  class="wf-flow__node"
                  @click="openStepByKey(n.key)"
                >
                  <rect
                    :x="n.x" :y="n.y" :width="n.w" :height="n.h"
                    rx="10"
                    :fill="n.fill"
                    :stroke="n.stroke"
                    :stroke-width="n.isStart ? 2.5 : 1.5"
                  />
                  <text :x="n.x + n.w / 2" :y="n.y + 21" text-anchor="middle" class="wf-flow__node-key">
                    {{ n.keyDisplay }}
                  </text>
                  <text :x="n.x + n.w / 2" :y="n.y + 38" text-anchor="middle" class="wf-flow__node-kind">
                    {{ n.isStart ? '▶ ' : '' }}{{ n.kindLabel }}{{ n.isEntry ? ' ⚑' : '' }}
                  </text>
                  <!-- Per-step actions: add-and-link / delete. -->
                  <text
                    :x="n.x + n.w - 12"
                    :y="n.y + 17"
                    text-anchor="middle"
                    class="wf-flow__node-menu"
                    @click.stop="openNodeMenu($event, n.key)"
                  >⋯</text>
                </g>
              </svg>
            </div>
            <div class="text-caption grey--text mt-1">
              Click a step to edit it. Arrows show where each answer leads. Use a box's ⋯ menu
              to add a linked step or delete it.
            </div>

            <!-- Node ⋯ menu (single instance, positioned at the click). -->
            <v-menu
              v-model="flowMenu.show"
              absolute
              :position-x="flowMenu.x"
              :position-y="flowMenu.y"
            >
              <v-list dense>
                <template v-if="flowMenuCanLink">
                  <v-subheader class="wf-flow__menu-header">Add linked step</v-subheader>
                  <v-list-item
                    v-for="k in stepKinds"
                    :key="k.value"
                    @click="addLinkedStep(flowMenu.stepKey, k.value)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small>{{ stepIcon(k.value) }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ k.text }}</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                </template>
                <v-list-item @click="deleteStepByKey(flowMenu.stepKey)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="error">mdi-delete-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="error--text">Delete step</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- ── JSON preview ──────────────────────────────────── -->
          <div v-show="editor.tab === 2">
            <div class="d-flex align-center mb-2" style="gap: 8px">
              <v-btn x-small outlined color="primary" @click="downloadDefinition">
                <v-icon x-small left>mdi-download</v-icon>Download
              </v-btn>
              <v-btn x-small outlined color="primary" @click="$refs.defFileInput.click()">
                <v-icon x-small left>mdi-upload</v-icon>Load from file
              </v-btn>
              <v-btn
                v-if="!editor.isNew && editor.workflow_id"
                x-small outlined
                @click="revertToSaved"
              >
                <v-icon x-small left>mdi-restore</v-icon>Revert to saved
              </v-btn>
              <input
                ref="defFileInput"
                type="file"
                accept=".json,application/json"
                style="display: none"
                @change="loadDefinitionFile"
              />
              <span class="text-caption grey--text">
                Loading replaces the editor contents — nothing persists until you save.
              </span>
            </div>
            <v-textarea
              :value="jsonPreview"
              readonly outlined rows="22"
              class="wf-json"
              hide-details
            />
          </div>

          <!-- ── History: this client's executions of this key ──── -->
          <div v-show="editor.tab === 3">
            <div v-if="editor.instances.loading" class="text-caption grey--text py-4">
              Loading executions…
            </div>
            <div v-else-if="!editor.instances.rows.length" class="text-caption grey--text py-4">
              No executions yet for this workflow.
            </div>
            <v-simple-table v-else dense>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Choice</th>
                  <th>Context</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Rating</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in editor.instances.rows" :key="row.instance_id">
                  <td>{{ row.user_name }}</td>
                  <td>{{ row.choice || '—' }}</td>
                  <td>{{ contextLabel(row.context) }}</td>
                  <td>
                    <v-chip x-small :color="statusColour(row.status)" dark>{{ row.status }}</v-chip>
                  </td>
                  <td>{{ formatInstanceDate(row.created) }}</td>
                  <td>
                    <template v-if="row.rating">
                      {{ row.rating.score }}<template v-if="row.rating.max"> / {{ row.rating.max }}</template>
                      <v-tooltip v-if="row.rating.comment" bottom max-width="360">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            x-small
                            color="indigo"
                            class="ml-1"
                            v-bind="attrs"
                            v-on="on"
                          >mdi-comment-text-outline</v-icon>
                        </template>
                        <span>{{ row.rating.comment }}</span>
                      </v-tooltip>
                    </template>
                    <template v-else>—</template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="editor.instances.rows.length >= 500" class="text-caption grey--text mt-1">
              Showing the most recent 500 executions.
            </div>
          </div>

          <!-- ── Stats: per-path summary + executions over time ── -->
          <div v-show="editor.tab === 4">
            <div v-if="editor.instances.loading" class="text-caption grey--text py-4">
              Loading executions…
            </div>
            <div v-else-if="!editor.instances.rows.length" class="text-caption grey--text py-4">
              No executions yet for this workflow.
            </div>
            <template v-else>
              <div class="text-caption font-weight-medium mb-1">By path</div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Path</th>
                    <th>Executions</th>
                    <th>Completions</th>
                    <th>Rated</th>
                    <th>Avg rating (rated)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in instanceStats.byPath" :key="p.path">
                    <td>{{ p.path }}</td>
                    <td>{{ p.total }}</td>
                    <td>{{ p.completed }} ({{ p.completedPct }}%)</td>
                    <td>{{ p.rated }} ({{ p.ratedPct }}%)</td>
                    <td>{{ p.avgRating === null ? '—' : p.avgRating }}</td>
                  </tr>
                  <tr class="font-weight-medium">
                    <td>All</td>
                    <td>{{ instanceStats.total }}</td>
                    <td>{{ instanceStats.completed }} ({{ instanceStats.completedPct }}%)</td>
                    <td>{{ instanceStats.rated }} ({{ instanceStats.ratedPct }}%)</td>
                    <td>{{ instanceStats.avgRating === null ? '—' : instanceStats.avgRating }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="text-caption font-weight-medium mt-4 mb-1">By context (page / surface)</div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Context</th>
                    <th>Executions</th>
                    <th>Completions</th>
                    <th>Rated</th>
                    <th>Avg rating (rated)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in instanceStats.byContext" :key="c.path">
                    <td>{{ c.path }}</td>
                    <td>{{ c.total }}</td>
                    <td>{{ c.completed }} ({{ c.completedPct }}%)</td>
                    <td>{{ c.rated }} ({{ c.ratedPct }}%)</td>
                    <td>{{ c.avgRating === null ? '—' : c.avgRating }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="text-caption font-weight-medium mt-4 mb-1">
                Executions — last 30 days
              </div>
              <div class="wf-stats-bars">
                <div
                  v-for="d in instanceStats.byDay"
                  :key="d.day"
                  class="wf-stats-bar"
                  :title="`${d.day}: ${d.count}`"
                >
                  <div
                    class="wf-stats-bar__fill"
                    :style="{ height: `${d.count ? Math.max(8, (d.count / instanceStats.maxDay) * 60) : 2}px` }"
                  ></div>
                </div>
              </div>
              <div class="d-flex justify-space-between text-caption grey--text" style="max-width: 640px">
                <span>{{ instanceStats.byDay[0] && instanceStats.byDay[0].day }}</span>
                <span>{{ instanceStats.byDay[instanceStats.byDay.length - 1] && instanceStats.byDay[instanceStats.byDay.length - 1].day }}</span>
              </div>
            </template>
          </div>

          <v-alert
            v-if="editor.validation && editor.validation.length === 0"
            dense text type="success" class="mt-3"
          >
            Definition is valid.
          </v-alert>
          <v-alert v-else-if="editor.validation" dense text type="warning" class="mt-3">
            <div v-for="(e, i) in editor.validation" :key="i">• {{ e }}</div>
          </v-alert>
        </v-card-text>

        <!-- Editing actions only make sense on the authoring tabs — the
             read-only History/Stats tabs hide them. -->
        <v-card-actions v-if="editor.tab < 3">
          <v-btn text small @click="validateDefinition">Validate</v-btn>
          <help-tip k="validation.publish" />
          <v-spacer />
          <div
            v-if="editor.message"
            class="text-caption mr-4"
            :class="editor.messageIsError ? 'error--text' : 'success--text'"
          >
            {{ editor.message }}
          </div>
          <!-- Deactivate = status-only transition on the saved row
               (definition untouched): hidden from users, history kept,
               Save & Publish brings it back. Same call as the list's
               archive button, surfaced here so retiring a workflow
               doesn't require finding the row icon. -->
          <v-btn
            v-if="!editor.isNew && editor.workflow_id && !editor.fromGlobal && editor.status !== 'inactive'"
            small text class="mr-2"
            :disabled="editor.saving"
            title="Hide this workflow from users without deleting it — Save & Publish reactivates"
            @click="deactivateFromEditor"
          >
            <v-icon small left>mdi-archive-outline</v-icon>Deactivate
          </v-btn>
          <!-- Save As = an independent copy under a new key (draft), so
               two variants of a flow can run in parallel — e.g. the
               chain-based create step vs the simplified generate one. -->
          <v-btn small text class="mr-2" :disabled="editor.saving" @click="openSaveAs">
            Save As…
          </v-btn>
          <v-btn small outlined class="mr-2" :loading="editor.saving" @click="save('draft')">
            Save Draft
          </v-btn>
          <v-btn small color="primary" :loading="editor.saving" @click="save('published')">
            Save &amp; Publish
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy from another client -->
    <v-dialog v-model="copyDialog.open" max-width="640">
      <v-card>
        <v-card-title class="text-subtitle-1 py-2 d-flex justify-space-between align-center flex-nowrap">
          <span>Copy a workflow from another client</span>
          <v-btn icon @click="copyDialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <div class="text-caption mb-3">
            The copy is remapped onto this client: template attributes are matched by their
            levelling-framework role, then by name; hierarchy types and content chains by name.
            Anything that can't be matched is flagged for a manual pick. The result opens in the
            editor as a new draft — nothing is saved until you save it.
          </div>
          <v-autocomplete
            v-model="copyDialog.selected"
            :items="copySourceItems"
            :loading="copyDialog.loading"
            label="Source workflow"
            dense outlined
            item-text="text"
            item-value="value"
            no-data-text="No other clients have workflows yet"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="copyDialog.open = false">Cancel</v-btn>
          <v-btn
            small color="primary"
            :disabled="!copyDialog.selected"
            :loading="copyDialog.preparing"
            @click="doCopyPrepare"
          >
            Copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save As: an independent copy of the OPEN definition. Its wire
         identity (workflow_key) is minted automatically — users only
         name the copy. -->
    <v-dialog v-model="saveAsDialog.open" max-width="520">
      <v-card>
        <v-card-title class="text-subtitle-1 py-2">Save as a new workflow</v-card-title>
        <v-card-text class="pt-3">
          <div class="text-caption mb-3">
            Saves the definition as it is in the editor — including unsaved changes — as a
            separate draft workflow. The original is not modified, so both can be tested in
            parallel; publish whichever wins.
          </div>
          <v-text-field
            v-model="saveAsDialog.name"
            label="New name"
            dense outlined
            :error-messages="saveAsDialog.error ? [saveAsDialog.error] : []"
            @input="saveAsDialog.error = ''"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="saveAsDialog.open = false">Cancel</v-btn>
          <v-btn small color="primary" :loading="editor.saving" @click="doSaveAs">
            Save copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="deleteDialog.open" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1">Delete workflow?</v-card-title>
        <v-card-text>
          Delete <strong>{{ deleteDialog.item && (deleteDialog.item.name || deleteDialog.item.workflow_key) }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="deleteDialog.open = false">Cancel</v-btn>
          <v-btn small color="error" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.open" :timeout="4000" :color="snack.colour">{{ snack.text }}</v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import HelpTip from './HelpTip.vue';
import HelpGuideDialog from './HelpGuideDialog.vue';
import { computeInstanceStats } from './workflowInstanceStats';

let uidCounter = 0;

/** Slug a label into a stable trigger/step key: "Start fresh" → "start_fresh". */
function slug(text) {
  const s = `${text || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return s || 'item';
}

/**
 * The builder's Control selects encode the pills presentation as
 * composite values ('select_pills' / 'radio_pills' / 'checkbox_pills');
 * the definition keeps type and `variant: 'pills'` separate. Encoding it
 * in the type keeps the row model a single field, so the field-by-field
 * load/serialize rebuilds can't silently strip the variant.
 */
function splitFieldControl(type) {
  // Composite encodings keep per-field flags inside the one Control
  // select (and safe from field-by-field rebuild whitelists):
  // `<type>_pills` = pills variant, `tree_free` = tree + free entry.
  if (type === 'tree_free') return { type: 'tree', pills: false, free: true };
  const pills = typeof type === 'string' && type.endsWith('_pills');
  return { type: (pills ? type.slice(0, -'_pills'.length) : type) || null, pills, free: false };
}
function joinFieldControl(type, variant, freeText) {
  // free_text with no explicit type means auto-resolved tree — encode
  // as tree_free either way (serializing type 'tree' is behaviourally
  // identical and keeps the flag round-tripping).
  if (freeText && (type === 'tree' || !type)) return 'tree_free';
  return (variant === 'pills' && type ? `${type}_pills` : type) || null;
}

/** The outgoing links of a builder-model step: [{ to, label }]. */
function stepEdges(step) {
  if (step.kind === 'choice')
    return step.options.filter((o) => o.to).map((o) => ({ to: o.to, label: o.label || o.key || '' }));
  if (step.kind === 'job_search' && step.selectedTo)
    return [
      {
        to: step.selectedTo,
        label: step.searchResultsMode === 'next_step' ? 'searched' : 'job selected',
      },
    ];
  if (step.kind === 'doc_list' && step.selectedTo)
    return [{ to: step.selectedTo, label: 'document selected' }];
  if (step.kind === 'form' && step.submittedTo)
    return [{ to: step.submittedTo, label: 'submitted' }];
  if (step.kind === 'doc_view' && step.confirmedTo)
    return [{ to: step.confirmedTo, label: 'confirmed' }];
  if (step.kind === 'doc_upload' && step.createdTo)
    return [{ to: step.createdTo, label: 'created' }];
  return [];
}

/**
 * Reading order: the start step first, then a breadth-first walk of the
 * links (so each step sits near the steps that lead to it); anything
 * unreachable trails at the end in its original order.
 */
function orderStepsLogically(steps) {
  if (!steps.length) return steps;
  const byKey = new Map(steps.map((s) => [s.step_key, s]));
  const start = steps.find((s) => s.is_start) || steps[0];
  const ordered = [];
  const visited = new Set();
  const queue = [start.step_key];
  while (queue.length) {
    const key = queue.shift();
    if (visited.has(key) || !byKey.has(key)) continue;
    visited.add(key);
    const step = byKey.get(key);
    ordered.push(step);
    for (const edge of stepEdges(step)) queue.push(edge.to);
  }
  for (const step of steps) if (!visited.has(step.step_key)) ordered.push(step);
  return ordered;
}

const FLOW_COLOURS = {
  choice: { fill: '#eef2ff', stroke: '#6366f1' },
  job_search: { fill: '#f0fdfa', stroke: '#0d9488' },
  doc_list: { fill: '#f0f9ff', stroke: '#0284c7' },
  doc_view: { fill: '#faf5ff', stroke: '#9333ea' },
  form: { fill: '#fff7ed', stroke: '#d97706' },
  terminal: { fill: '#f0fdf4', stroke: '#16a34a' },
};

function blankStep(kind, key) {
  return {
    uid: ++uidCounter,
    step_key: key,
    kind,
    is_start: false,
    // Entry point: this step can be started into directly by intent
    // ("I want to upload a profile" → the upload step). The hint is the
    // phrase the model matches the user's request against.
    isEntry: false,
    entryHint: '',
    prompt: '',
    guidance: '',
    requiresAction: null,
    requiresTmplNames: [],
    // choice
    options: [],
    // job_search / doc_list share selectedTo
    placeholder: '',
    selectedTo: null,
    searchDocTypes: [],
    searchFields: [],
    searchHideKeyword: false,
    searchResultsMode: 'inline',
    // doc_list canned query / previous-search source
    docListMode: 'query',
    docListQuery: '',
    docListTypes: [],
    docListStatuses: [],
    docListLimit: null,
    docListSearchStep: null,
    docListOpenLabel: '',
    // doc_view
    docViewSourceStep: null,
    confirmLabel: '',
    confirmedTo: null,
    // doc_upload ('extract' = chain-free Atlas extraction, 'chain' = legacy prompt chain)
    uploadTmplName: '',
    uploadMode: 'extract',
    uploadExtractParts: [],
    uploadExtractContext: '',
    uploadChainId: null,
    uploadDocumentInput: null,
    uploadAccept: '',
    uploadConfirmLabel: '',
    createdTo: null,
    // form
    submitLabel: '',
    fieldsMode: 'template',
    templateName: '',
    templateAttrs: [],
    staticFields: [],
    submittedTo: null,
    // form → on-submit create behaviour
    // ('none' | 'chain' | 'direct' | 'user_choice' | 'generate' | 'generate_user_choice')
    createMode: 'none',
    createChainId: null,
    // form → simplified content generation (create_document.generate):
    // sections to draft [{ tp_id, part (name fallback), instructions,
    // format ''|'text'|'items', minItems, maxItems }], the global
    // tone/context, and the post-capability switches.
    generateParts: [],
    generateContext: '',
    generatePostSkills: false,
    generatePostProficiency: false,
    // Target tag part for the skills capabilities (blank = auto-detect
    // when the template has exactly one tag part).
    generateSkillsPart: null,
    // hold the submit while similar existing titles are shown (direct
    // name match, else grounded AI suggestions from the catalogue)
    titleTriage: false,
    // offer to add an unknown title to the linked-docs hierarchy
    createMissingNode: false,
    // round-trip safety: props this builder doesn't know about.
    _extraProps: {},
  };
}

export default {
  name: 'adminAtlasWorkflows',
  components: { HelpTip, HelpGuideDialog },
  data() {
    return {
      loading: false,
      workflows: [],
      templates: [],
      promptChains: [],
      searchFieldOptions: [],
      searchValueModes: [
        { text: 'Free text', value: 'none' },
        { text: 'Fixed list', value: 'static' },
        { text: 'From template attribute', value: 'template' },
        { text: 'From hierarchy', value: 'hierarchy' },
      ],
      hierarchies: [],
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Status', value: 'status' },
        { text: 'MCP', value: 'mcp_visible', sortable: false },
        { text: 'Steps', value: 'step_count' },
        { text: 'Instances', value: 'instance_count' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      stepKinds: [
        { text: 'Choice (buttons)', value: 'choice' },
        { text: 'Job search', value: 'job_search' },
        { text: 'Document list', value: 'doc_list' },
        { text: 'Document view', value: 'doc_view' },
        { text: 'Document upload', value: 'doc_upload' },
        // Wire block_type stays 'form' (closed contract vocabulary shared
        // with the MCP bundle) — 'Document create' is what every authored
        // form actually is; capture-only forms are the 'Continue the
        // workflow only' On-submit option.
        { text: 'Document create (form)', value: 'form' },
        { text: 'End point', value: 'terminal' },
      ],
      requiresOptions: [
        { text: 'View documents', value: 'view' },
        { text: 'Edit documents', value: 'edit' },
        { text: 'Create documents', value: 'create' },
      ],
      attrTypeOptions: [
        { text: 'Auto (from the attribute)', value: null },
        { text: 'Text', value: 'text' },
        { text: 'Multi-line text', value: 'textarea' },
        { text: 'Dropdown', value: 'select' },
        { text: 'Pills (dropdown when over 5 values)', value: 'select_pills' },
        { text: 'Radio buttons', value: 'radio' },
        { text: 'Pills (single pick, always)', value: 'radio_pills' },
        { text: 'Checkboxes (multi)', value: 'checkbox' },
        { text: 'Pills (multi, always)', value: 'checkbox_pills' },
        { text: 'Tree picker', value: 'tree' },
        { text: 'Tree picker — allow typing new values', value: 'tree_free' },
      ],
      // Hierarchy fields are value-constrained by definition — free-text
      // controls don't apply (the engine rejects text/textarea for them).
      // "Tree — allow typing" is the exception: picks stay node-backed,
      // typed entries submit { value } with no node (pair with title
      // triage so typed titles get the similar-role check).
      hierarchyTypeOptions: [
        { text: 'Auto (tree picker)', value: null },
        { text: 'Tree picker', value: 'tree' },
        { text: 'Tree picker — allow typing new values', value: 'tree_free' },
        { text: 'Dropdown (flattened)', value: 'select' },
        { text: 'Pills (flattened — dropdown when over 5 values)', value: 'select_pills' },
        { text: 'Radio buttons (flattened)', value: 'radio' },
        { text: 'Pills (flattened, single pick)', value: 'radio_pills' },
        { text: 'Checkboxes (multi, flattened)', value: 'checkbox' },
        { text: 'Pills (flattened, multi)', value: 'checkbox_pills' },
      ],
      staticTypeOptions: [
        { text: 'Text', value: 'text' },
        { text: 'Multi-line text', value: 'textarea' },
        { text: 'Dropdown', value: 'select' },
        { text: 'Pills (dropdown when over 5 values)', value: 'select_pills' },
        { text: 'Radio buttons', value: 'radio' },
        { text: 'Pills (single pick, always)', value: 'radio_pills' },
        { text: 'Checkboxes (multi)', value: 'checkbox' },
        { text: 'Pills (multi, always)', value: 'checkbox_pills' },
      ],
      createModeOptions: [
        { text: 'Continue the workflow only', value: 'none' },
        { text: 'Create document — generate content (simple)', value: 'generate' },
        { text: 'Create document — user choice to generate (simple)', value: 'generate_user_choice' },
        { text: 'Create document — generate content with a chain', value: 'chain' },
        { text: 'Create document — user choice to generate with a chain', value: 'user_choice' },
        { text: 'Create document — directly from the captured fields', value: 'direct' },
      ],
      generateFormatOptions: [
        { text: 'Auto (from the part)', value: '' },
        { text: 'Single text', value: 'text' },
        { text: 'List of items', value: 'items' },
      ],
      // Tag-part row choices in the unified list — proficiency extracts
      // internally, so the options are exclusive.
      tagChoiceOptions: [
        { text: 'Ignored', value: 'none' },
        { text: 'Extracted skills (no levels)', value: 'skills' },
        { text: 'Skills with proficiency levels', value: 'proficiency' },
      ],
      // Flow-view ⋯ menu state (one menu, positioned at the click).
      flowMenu: { show: false, x: 0, y: 0, stepKey: null },
      editor: {
        open: false,
        // Tab 1 = Flow — the default view when the editor opens; the
        // Steps tab (0) is where openStepByKey lands on a node click.
        tab: 1,
        openStep: 0,
        isNew: false,
        fromGlobal: false,
        workflow_id: null,
        // The saved row's current status — drives the editor's
        // Deactivate action (list rows have their own archive button).
        status: 'draft',
        workflow_key: '',
        name: '',
        description: '',
        // Atlas's opener on pages that auto-start this workflow
        // (atlas_context_workflow); '' = the standard greeting.
        introMessage: '',
        // Whether MCP hosts (Claude etc.) may list/start this workflow.
        mcpVisible: true,
        // Exit-adjacent feedback score widget (workflow-level config).
        feedback: {
          enabled: false,
          label: 'How easy was it to get this done?',
          count: 5,
          pre: 'Difficult',
          post: 'Easy',
          triggerMax: 0,
          prompt: '',
        },
        steps: [],
        validation: null,
        saving: false,
        message: '',
        messageIsError: false,
        // Execution history for the History/Stats tabs — fetched lazily
        // the first time either tab is opened.
        instances: { loading: false, loaded: false, rows: [] },
        // Cross-client copy provenance: the match report + a "copied
        // from" label, shown until the editor is next opened.
        copyReport: null,
        copySource: '',
      },
      copyDialog: { open: false, loading: false, sources: [], selected: null, preparing: false },
      saveAsDialog: { open: false, name: '', error: '' },
      deleteDialog: { open: false, item: null },
      snack: { open: false, text: '', colour: 'success' },
    };
  },
  watch: {
    // Lazy-load the execution history the first time the History or
    // Stats tab is opened (unsaved new workflows have none to load).
    'editor.tab'(tab) {
      if (
        (tab === 3 || tab === 4) &&
        !this.editor.instances.loaded &&
        !this.editor.instances.loading &&
        !this.editor.isNew &&
        this.editor.workflow_key
      ) {
        this.fetchInstances();
      }
    },
  },
  computed: {
    /**
     * User-facing step labels — step keys are internal wiring (auto-
     * generated, never shown). A step is labelled by its card prompt
     * (truncated) when it has one, else by its kind; duplicates get a
     * counter so target-step pickers stay unambiguous.
     */
    stepLabelByKey() {
      const counts = {};
      const labels = {};
      for (const s of this.editor.steps || []) {
        const snippet = String(s.prompt || '').trim();
        const base = snippet
          ? snippet.length > 48
            ? `${snippet.slice(0, 45).trim()}…`
            : snippet
          : this.kindLabel(s.kind);
        counts[base] = (counts[base] || 0) + 1;
        labels[s.step_key] = counts[base] > 1 ? `${base} (${counts[base]})` : base;
      }
      return labels;
    },
    /** Copy-source picker rows, one per other-client workflow. */
    copySourceItems() {
      return (this.copyDialog.sources || []).map((s) => ({
        text: `${s.client_name} — ${s.name} (${s.status}, ${s.step_count} steps)`,
        value: s.workflow_id,
      }));
    },
    /** Aggregates for the Stats tab, derived from the history rows. */
    instanceStats() {
      return computeInstanceStats(this.editor.instances.rows || []);
    },
    /** "Ask why at or below" options: Never + 1..count. */
    feedbackTriggerItems() {
      const count = Number(this.editor.feedback.count) || 5;
      return [
        { text: 'Never', value: 0 },
        ...Array.from({ length: count }, (_, i) => ({ text: `${i + 1}`, value: i + 1 })),
      ];
    },
    /** The ⋯ menu's source step can take an outgoing link (not terminal). */
    flowMenuCanLink() {
      const step = this.editor.steps.find((s) => s.step_key === this.flowMenu.stepKey);
      return !!step && step.kind !== 'terminal';
    },
    jsonPreview() {
      try {
        const feedback = this.feedbackPayload();
        const intro = this.introMessagePayload();
        return JSON.stringify(
          {
            ...(intro ? { intro_message: intro } : {}),
            ...(feedback ? { feedback } : {}),
            ...(this.editor.mcpVisible ? {} : { mcp_visible: false }),
            ...this.buildDefinition(),
          },
          null,
          2,
        );
      } catch (e) {
        return `// ${e.message}`;
      }
    },
    /**
     * Levelled left-to-right layout: BFS depth from the start step =
     * column; unreachable steps get a trailing column. Edges are cubic
     * curves labelled with the answer that follows them.
     */
    flowGraph() {
      const steps = this.editor.steps;
      const NODE_W = 172;
      const NODE_H = 52;
      const COL_W = 250;
      const ROW_H = 92;
      const MARGIN = 24;

      // BFS levels from the start step.
      const byKey = new Map(steps.map((s) => [s.step_key, s]));
      const levels = new Map();
      const start = steps.find((s) => s.is_start) || steps[0];
      if (start) {
        const queue = [{ key: start.step_key, lvl: 0 }];
        while (queue.length) {
          const { key, lvl } = queue.shift();
          if (!byKey.has(key) || levels.has(key)) continue;
          levels.set(key, lvl);
          for (const edge of stepEdges(byKey.get(key))) queue.push({ key: edge.to, lvl: lvl + 1 });
        }
      }
      let maxLevel = 0;
      for (const lvl of levels.values()) maxLevel = Math.max(maxLevel, lvl);
      const strayLevel = levels.size < steps.length ? maxLevel + 1 : maxLevel;
      for (const s of steps) if (!levels.has(s.step_key)) levels.set(s.step_key, strayLevel);

      // Stack nodes within their column, in current step order.
      const rowsPerLevel = new Map();
      const nodes = [];
      const nodeByKey = new Map();
      for (const s of steps) {
        const lvl = levels.get(s.step_key);
        const row = rowsPerLevel.get(lvl) || 0;
        rowsPerLevel.set(lvl, row + 1);
        const label = this.stepLabelFor(s.step_key);
        const node = {
          key: s.step_key,
          keyDisplay: label.length > 22 ? `${label.slice(0, 21)}…` : label,
          kindLabel: this.kindLabel(s.kind),
          isStart: s.is_start === true,
          isEntry: s.isEntry === true,
          x: MARGIN + lvl * COL_W,
          y: MARGIN + row * ROW_H,
          w: NODE_W,
          h: NODE_H,
          ...(FLOW_COLOURS[s.kind] || FLOW_COLOURS.terminal),
        };
        nodes.push(node);
        nodeByKey.set(s.step_key, node);
      }

      // Edges with slight vertical fan-out when a node has several.
      const edges = [];
      for (const s of steps) {
        const from = nodeByKey.get(s.step_key);
        const outgoing = stepEdges(s).filter((e) => nodeByKey.has(e.to));
        outgoing.forEach((edge, i) => {
          const to = nodeByKey.get(edge.to);
          const spread = outgoing.length > 1 ? (i - (outgoing.length - 1) / 2) * 12 : 0;
          const x1 = from.x + from.w;
          const y1 = from.y + from.h / 2 + spread;
          const x2 = to.x - 4;
          const y2 = to.y + to.h / 2;
          const bend = x2 > x1 ? 55 : 80; // backward links get a wider loop
          edges.push({
            path: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
            label: edge.label,
            labelX: (x1 + x2) / 2,
            labelY: (y1 + y2) / 2 - 6 + spread,
          });
        });
      }

      let maxRows = 1;
      for (const rows of rowsPerLevel.values()) maxRows = Math.max(maxRows, rows);
      return {
        nodes,
        edges,
        width: MARGIN * 2 + strayLevel * COL_W + NODE_W,
        height: MARGIN * 2 + Math.max(1, maxRows) * ROW_H,
      };
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    // ── data ──────────────────────────────────────────────────
    loadAll() {
      this.loading = true;
      axios
        .get('agent/workflows/admin/list')
        .then((r) => {
          if (r.data.Status === 'OK') this.workflows = r.data.Data;
        })
        .finally(() => (this.loading = false));
      axios.get('agent/workflows/admin/template-attrs').then((r) => {
        if (r.data.Status === 'OK') this.templates = r.data.Data;
      });
      axios.get('agent/workflows/admin/hierarchies').then((r) => {
        if (r.data.Status === 'OK') this.hierarchies = r.data.Data || [];
      });
      axios.get('agent/workflows/admin/prompt-chains').then((r) => {
        if (r.data.Status === 'OK') this.promptChains = r.data.Data || [];
      });
      axios.get('agent/workflows/admin/search-fields').then((r) => {
        if (r.data.Status === 'OK') {
          this.searchFieldOptions = (r.data.Data || []).map((f) => ({
            text: `${f.text || f.attr} (${f.attr})`,
            value: f.attr,
          }));
        }
      });
    },
    /** Chip colours: workflow statuses (list) + instance statuses (History). */
    statusColour(status) {
      return (
        {
          published: 'success',
          inactive: 'grey',
          draft: 'orange',
          completed: 'success',
          cancelled: 'grey',
          active: 'indigo',
        }[status] || 'grey'
      );
    },
    hierarchyLevelItems(htId) {
      const ht = this.hierarchies.find((h) => h.hierarchy_type_id === htId);
      if (!ht) return [];
      return (ht.level_names || []).map((name, i) => ({
        text: `${i + 1} — ${name}`,
        value: i + 1,
      }));
    },
    templateAttrItems(templateName) {
      const tmpl = this.templates.find((t) => t.tmpl_name === templateName);
      if (!tmpl) return [];
      return tmpl.attrs.map((a) => {
        let note = '';
        if (a.restrict_to_lookup && !a.dependent_lookup) note = ' · lookup values';
        else if (a.dependent_lookup) note = ' · dependent lookup';
        else if (a.hr_override) note = ` · hierarchy level ${a.hr_override.level}`;
        else if (
          a.levelling_framework_attribute &&
          !['FAMILY', 'LOCATION', 'BUSINESS UNIT'].includes(a.levelling_framework_attribute)
        ) {
          note = ' · levelling framework values';
        }
        return { text: `${a.title} — ${a.part}${note}`, value: a.tpa_id };
      });
    },
    /** Form-field picker items: the template's attributes plus the
     * client's hierarchy types (encoded as 'ht:<id>' values — a row
     * holding one is a hierarchy treepicker field, not an attr). */
    formFieldItems(templateName) {
      const items = this.templateAttrItems(templateName);
      if (this.hierarchies.length) {
        items.push({ header: 'Hierarchy types — treepicker of the hierarchy' });
        for (const ht of this.hierarchies) {
          items.push({ text: `Hierarchy: ${ht.name}`, value: `ht:${ht.hierarchy_type_id}` });
        }
      }
      return items;
    },
    isGenerateMode(step) {
      return step.createMode === 'generate' || step.createMode === 'generate_user_choice';
    },
    // ── Unified field/section list ─────────────────────────────
    /**
     * One row per template part / capturable attribute / hierarchy
     * type, in template order. Each row carries a live reference to its
     * templateAttrs entry (`fa`) or generateParts entry (`gp`) when the
     * row is active, so the inline controls bind straight onto the
     * models the serializer already reads — this list is a VIEW, not a
     * third model. Excluded outright: label attrs (server-side), locked
     * attrs/parts, tag attrs (the skills select owns tag parts), child
     * parts (they ride their parent's shape). Derived attrs render
     * inert.
     */
    unifiedRows(step) {
      const tmpl = this.resolveTemplateByName(step.templateName);
      if (!tmpl) return [];
      const rows = [];
      // Hierarchies first: the placement usually IS the first question
      // the form asks (which job / which grade).
      if (this.hierarchies.length) {
        rows.push({ key: 'hsec', kind: 'section', label: 'Hierarchies (placement pickers)' });
        for (const ht of this.hierarchies) {
          rows.push({
            key: `h${ht.hierarchy_type_id}`,
            kind: 'hierarchy',
            ht,
            fa: this.findInputEntry(step, `ht:${ht.hierarchy_type_id}`),
          });
        }
        rows.push({ key: 'psec', kind: 'section', label: 'Template sections' });
      }
      const allParts = tmpl.parts || [];
      const parts = allParts.filter((p) => p.parent_tp_id === null && !p.locked);
      for (const part of parts) {
        const attrs = (tmpl.attrs || []).filter(
          (a) => a.tp_id === part.tp_id && !a.locked && !a.tag,
        );
        const capturable = attrs.filter((a) => !this.isDerivedAttr(a));
        const derived = attrs.filter((a) => this.isDerivedAttr(a));
        // Decoration (section headings, dividers): fixed default content
        // and nothing to capture — not part of this conversation.
        if (!attrs.length && part.fixed_text) continue;
        const child = allParts.find((p) => p.parent_tp_id === part.tp_id && !p.locked) || null;
        if (part.has_tag) {
          // Tag parts are owned by the Skills select.
          rows.push({ key: `p${part.tp_id}`, kind: 'part', part, child, gp: null, inputAttrCount: 0 });
          continue;
        }
        if (!capturable.length && derived.length) {
          // The part exists to display a derived value (Job Level) —
          // one inert row, no separate attr line.
          rows.push({ key: `p${part.tp_id}`, kind: 'derived', attr: derived[0], partName: part.tp_name });
          continue;
        }
        if (capturable.length === 1 && !derived.length) {
          // The overwhelmingly common shape: one part, one same-named
          // attribute — ONE row with a single input/output role, not a
          // confusing pair.
          const attr = capturable[0];
          rows.push({
            key: `p${part.tp_id}`,
            kind: 'merged',
            part,
            child,
            attr,
            fa: this.findInputEntry(step, attr.tpa_id),
            gp: this.findOutputEntry(step, part),
          });
          continue;
        }
        const inputAttrCount = capturable.filter((a) => this.findInputEntry(step, a.tpa_id)).length;
        rows.push({
          key: `p${part.tp_id}`,
          kind: 'part',
          part,
          child,
          gp: this.findOutputEntry(step, part),
          inputAttrCount,
        });
        for (const attr of attrs) {
          rows.push(
            this.isDerivedAttr(attr)
              ? { key: `a${attr.tpa_id}`, kind: 'derived', attr }
              : { key: `a${attr.tpa_id}`, kind: 'attr', attr, fa: this.findInputEntry(step, attr.tpa_id) },
          );
        }
      }
      return rows;
    },
    /** The hierarchy type a row's value is force-written from
     * (attr- or part-level hr_override), or null. */
    hrOverrideHt(row) {
      if ((row.kind === 'merged' || row.kind === 'attr') && row.attr.hr_override) {
        return row.attr.hr_override.ht_id || null;
      }
      if (row.part && row.part.hr_override) return row.part.hr_override.ht_id || null;
      return null;
    },
    /** ht_id when the row's value derives from a hierarchy the form
     * CAPTURES as input — the value follows the placement, so there is
     * nothing to ask or draft. */
    derivedFromInput(step, row) {
      const htId = this.hrOverrideHt(row);
      return htId && this.findInputEntry(step, `ht:${htId}`) ? htId : null;
    },
    htName(htId) {
      const ht = this.hierarchies.find((h) => h.hierarchy_type_id === htId);
      return ht ? ht.name : `hierarchy ${htId}`;
    },
    mergedRole(row) {
      return row.fa ? 'input' : row.gp ? 'output' : 'ignore';
    },
    mergedRoleItems(step, row) {
      return [
        { text: 'Ignored', value: 'ignore' },
        { text: 'Ask on the form (input)', value: 'input' },
        ...(this.isGenerateMode(step) && !row.part.has_tag
          ? [{ text: 'Draft with Atlas (output)', value: 'output' }]
          : []),
      ];
    },
    setMergedRole(step, row, role) {
      // Input and output are mutually exclusive for a merged row — the
      // engine treats captured values as authoritative for their part.
      this.setAttrRole(step, row, role === 'input' ? 'input' : 'ignore');
      this.setPartRole(step, row, role === 'output' ? 'output' : 'ignore');
    },
    /** The engine writes these itself (derivedAttrsFromSubmission) —
     * never asked on the form. */
    isDerivedAttr(attr) {
      return (
        `${attr.levelling_framework_attribute || ''}`.trim().toUpperCase() ===
        'LEVEL TRACK ABBREVIATION'
      );
    },
    findInputEntry(step, tpaId) {
      return (step.templateAttrs || []).find((fa) => fa.tpa_id === tpaId) || null;
    },
    findOutputEntry(step, part) {
      return (
        (step.generateParts || []).find(
          (gp) =>
            gp.tp_id === part.tp_id ||
            (!gp.tp_id && gp.part && this.resolveTpIdByPartName(step.templateName, gp.part) === part.tp_id),
        ) || null
      );
    },
    partHint(row) {
      const bits = [row.part.multi ? 'list' : 'single text'];
      if (row.child) bits.push(`+ ${row.child.tp_name} rows`);
      if (row.part.has_tag) bits.push('tags');
      return `(${bits.join(', ')})`;
    },
    attrHint(attr) {
      if (attr.hr_override) return '· hierarchy-linked';
      if (attr.restrict_to_lookup) return '· lookup values';
      if (attr.levelling_framework_attribute) return `· ${attr.levelling_framework_attribute.toLowerCase()} values`;
      return '';
    },
    setAttrRole(step, row, role) {
      if (role === 'input') {
        if (!row.fa) {
          this.placeInputEntry(step, {
            tpa_id: row.attr.tpa_id,
            attr: null,
            part: null,
            htLevel: null,
            label: '',
            type: null,
            required: false,
            placeholder: '',
          });
        }
      } else {
        const at = step.templateAttrs.findIndex((fa) => fa.tpa_id === row.attr.tpa_id);
        if (at >= 0) step.templateAttrs.splice(at, 1);
      }
    },
    setHierarchyRole(step, row, role) {
      const encoded = `ht:${row.ht.hierarchy_type_id}`;
      if (role === 'input') {
        if (!row.fa) {
          const levels = this.hierarchyLevelItems(row.ht.hierarchy_type_id);
          this.placeInputEntry(step, {
            tpa_id: encoded,
            attr: null,
            part: null,
            // Deepest enabled level — the document-link level in practice.
            htLevel: levels.length ? levels[levels.length - 1].value : null,
            label: '',
            type: null,
            required: false,
            placeholder: '',
          });
        }
      } else {
        const at = step.templateAttrs.findIndex((fa) => fa.tpa_id === encoded);
        if (at >= 0) step.templateAttrs.splice(at, 1);
      }
    },
    setPartRole(step, row, role) {
      if (role === 'output') {
        if (!row.gp) {
          step.generateParts.push({
            tp_id: row.part.tp_id,
            part: null,
            instructions: '',
            format: '',
            minItems: null,
            maxItems: null,
            childMin: null,
            childMax: null,
          });
          this.syncGeneratePartOrder(step);
        }
      } else {
        const at = step.generateParts.findIndex(
          (gp) =>
            gp.tp_id === row.part.tp_id ||
            (!gp.tp_id && gp.part && this.resolveTpIdByPartName(step.templateName, gp.part) === row.part.tp_id),
        );
        if (at >= 0) step.generateParts.splice(at, 1);
      }
    },
    /** Template-order rank of every input-capable row (attrs, merged
     * part+attr rows, hierarchies) and every part row, keyed the way
     * templateAttrs / generateParts reference them. */
    templateOrderRanks(step) {
      const attrOrder = new Map();
      const partOrder = new Map();
      this.unifiedRows(step).forEach((row, i) => {
        if (row.kind === 'attr' || row.kind === 'merged') attrOrder.set(row.attr.tpa_id, i);
        if (row.kind === 'hierarchy') attrOrder.set(`ht:${row.ht.hierarchy_type_id}`, i);
        if (row.kind === 'part' || row.kind === 'merged') partOrder.set(row.part.tp_id, i);
      });
      return { attrOrder, partOrder };
    },
    /** Add a new form input at its template-order position relative to
     * the entries already present, WITHOUT re-sorting them — the
     * templateAttrs array order is the authored form order (it
     * serializes verbatim and the engine asks fields in that order),
     * so an order the author set by hand must survive role toggles. */
    placeInputEntry(step, entry) {
      const { attrOrder } = this.templateOrderRanks(step);
      const rank = (key) => (attrOrder.has(key) ? attrOrder.get(key) : Number.MAX_SAFE_INTEGER);
      const r = rank(entry.tpa_id);
      const at = step.templateAttrs.findIndex((fa) => rank(fa.tpa_id) > r);
      if (at === -1) step.templateAttrs.push(entry);
      else step.templateAttrs.splice(at, 0, entry);
    },
    /** Keep drafted sections in template order — output order has no
     * authored meaning (the document renders parts in template order),
     * so canonical order keeps the serialized definition stable.
     * Entries the list can't place (unresolved) sink to the end,
     * keeping their relative order. */
    syncGeneratePartOrder(step) {
      const { partOrder } = this.templateOrderRanks(step);
      const rank = (key) => (partOrder.has(key) ? partOrder.get(key) : Number.MAX_SAFE_INTEGER);
      step.generateParts = [...step.generateParts].sort(
        (a, b) =>
          rank(a.tp_id || this.resolveTpIdByPartName(step.templateName, a.part)) -
          rank(b.tp_id || this.resolveTpIdByPartName(step.templateName, b.part)),
      );
    },
    /** Display name for a form-order row: the label override if set,
     * else the hierarchy / attribute the entry captures. */
    inputEntryLabel(step, fa) {
      if (fa.label) return fa.label;
      const htId = this.formFieldHtId(fa);
      if (htId) return `${this.htName(htId)} (hierarchy)`;
      const tmpl = this.resolveTemplateByName(step.templateName);
      const attr = tmpl && (tmpl.attrs || []).find((a) => a.tpa_id === fa.tpa_id);
      return attr ? attr.title : fa.attr || `field ${fa.tpa_id}`;
    },
    /** In chain mode, the chain's Document Formatter defines the outputs
     * — annotate the part rows it writes (refdata output_part_ids). */
    chainWritesPart(step, tpId) {
      if (step.createMode !== 'chain' && step.createMode !== 'user_choice') return false;
      const chain = this.promptChains.find((c) => c.prompt_chain_id === step.createChainId);
      return !!chain && Array.isArray(chain.output_part_ids) && chain.output_part_ids.includes(tpId);
    },
    /** The skills selection for a tag part row — reads/writes the
     * existing generatePostSkills / generatePostProficiency /
     * generateSkillsPart model (one skills target at a time; proficiency
     * extracts internally, so the choices are exclusive). */
    tagChoice(step, part) {
      const targeted = step.generateSkillsPart
        ? step.generateSkillsPart === part.tp_name
        : this.tagPartItems(step.templateName).length === 1;
      if (!targeted) return 'none';
      if (step.generatePostProficiency) return 'proficiency';
      if (step.generatePostSkills) return 'skills';
      return 'none';
    },
    setTagChoice(step, part, value) {
      if (value === 'none') {
        if (this.tagChoice(step, part) !== 'none') {
          step.generatePostSkills = false;
          step.generatePostProficiency = false;
          step.generateSkillsPart = null;
        }
        return;
      }
      step.generateSkillsPart = part.tp_name;
      step.generatePostSkills = value === 'skills';
      step.generatePostProficiency = value === 'proficiency';
    },
    /** Configured entries the unified list can't place on the current
     * template — surfaced with a remove button instead of vanishing. */
    unresolvedEntries(step) {
      const tmpl = this.resolveTemplateByName(step.templateName);
      const out = [];
      (step.templateAttrs || []).forEach((fa, i) => {
        const htId = this.formFieldHtId(fa);
        const known = htId
          ? this.hierarchies.some((h) => h.hierarchy_type_id === htId)
          : !!tmpl &&
            tmpl.attrs.some(
              (a) => a.tpa_id === fa.tpa_id && !a.locked && !a.tag && !this.isDerivedAttr(a),
            );
        if (!known) {
          out.push({
            label: `field: ${fa.attr || fa.label || fa.tpa_id || '(unset)'}`,
            remove: () => step.templateAttrs.splice(i, 1),
          });
        }
      });
      (step.generateParts || []).forEach((gp, i) => {
        const tpId = gp.tp_id || this.resolveTpIdByPartName(step.templateName, gp.part);
        const known =
          !!tmpl &&
          (tmpl.parts || []).some(
            (p) =>
              p.tp_id === tpId &&
              p.parent_tp_id === null &&
              !p.locked &&
              !p.has_tag &&
              // Decoration parts (fixed text, no capturable attrs) have
              // no row in the list — an output on one must be removable.
              !(
                p.fixed_text &&
                !(tmpl.attrs || []).some((a) => a.tp_id === p.tp_id && !a.locked && !a.tag)
              ),
          );
        if (!known) {
          out.push({
            label: `drafted section: ${gp.part || gp.tp_id || '(unset)'}`,
            remove: () => step.generateParts.splice(i, 1),
          });
        }
      });
      return out;
    },
    /** Top-level parts of the selected template — the upload extract
     * section picker. Shape hints ride the label (list / child rows).
     * Same exclusions as the create step's output candidates: locked
     * parts, tag parts (skills — no post capabilities on upload), and
     * decoration parts (fixed text, nothing capturable). */
    templatePartItems(templateName) {
      const tmpl = this.resolveTemplateByName(templateName);
      const parts = (tmpl && tmpl.parts) || [];
      const attrs = (tmpl && tmpl.attrs) || [];
      const childOf = (tpId) => parts.find((p) => p.parent_tp_id === tpId && !p.locked);
      return parts
        .filter(
          (p) =>
            p.parent_tp_id === null &&
            !p.locked &&
            !p.has_tag &&
            !(p.fixed_text && !attrs.some((a) => a.tp_id === p.tp_id && !a.locked && !a.tag)),
        )
        .map((p) => {
          const child = childOf(p.tp_id);
          const hints = [
            p.multi ? 'list' : 'single text',
            ...(child ? [`+ ${child.tp_name} rows`] : []),
          ];
          return { text: `${p.tp_name} (${hints.join(', ')})`, value: p.tp_id };
        });
    },
    /** Tag-attributed parts — where extracted/rated skills can land. */
    tagPartItems(templateName) {
      const tmpl = this.resolveTemplateByName(templateName);
      return ((tmpl && tmpl.parts) || [])
        .filter((p) => p.has_tag)
        .map((p) => ({ text: p.tp_name, value: p.tp_name }));
    },
    addGeneratePart(step) {
      step.generateParts.push({
        tp_id: null,
        part: null,
        instructions: '',
        format: '',
        minItems: null,
        maxItems: null,
      });
    },
    addUploadExtractPart(step) {
      step.uploadExtractParts.push({
        tp_id: null,
        part: null,
        instructions: '',
        format: '',
        minItems: null,
        maxItems: null,
        childMin: null,
        childMax: null,
      });
    },
    /** Prompt chains bound to the selected template (tmpl_id match). */
    templateChainItems(templateName) {
      const tmpl = this.resolveTemplateByName(templateName);
      if (!tmpl) return [];
      return this.promptChains
        .filter((c) => c.tmpl_id === tmpl.tmpl_id)
        .map((c) => ({ text: c.chain_name, value: c.prompt_chain_id }));
    },
    /** Input property keys of a chain — the doc_upload document_input picker. */
    chainInputItems(chainId) {
      const chain = this.promptChains.find((c) => c.prompt_chain_id === chainId);
      return chain && Array.isArray(chain.input_keys) ? chain.input_keys : [];
    },
    /** ht_id when the row references a hierarchy type, else null. */
    formFieldHtId(fa) {
      return typeof fa.tpa_id === 'string' && fa.tpa_id.startsWith('ht:')
        ? Number(fa.tpa_id.slice(3))
        : null;
    },
    onFormFieldChange(fa) {
      fa.attr = null;
      fa.part = null;
      const htId = this.formFieldHtId(fa);
      if (htId) {
        // Default to the deepest enabled level — "place the document" is
        // almost always a leaf pick; the author can move it up.
        const ht = this.hierarchies.find((h) => h.hierarchy_type_id === htId);
        fa.htLevel = ht && ht.level_names.length ? ht.level_names.length : 1;
        // Free-text overrides don't apply to hierarchy fields.
        if (fa.type === 'text' || fa.type === 'textarea') fa.type = null;
        fa.placeholder = '';
      } else {
        fa.htLevel = null;
      }
    },
    /** True when the (possibly pills-composite) control carries options. */
    isOptionControl(type) {
      return ['select', 'radio', 'checkbox'].includes(splitFieldControl(type).type);
    },
    /** Execution history for the History/Stats tabs (lazy, once per open). */
    fetchInstances() {
      this.editor.instances.loading = true;
      axios
        .get(`agent/workflows/admin/instances/${encodeURIComponent(this.editor.workflow_key)}`)
        .then((r) => {
          this.editor.instances.rows = r.data.Status === 'OK' ? r.data.Data || [] : [];
          this.editor.instances.loaded = true;
        })
        .catch(() => {
          this.editor.instances.rows = [];
        })
        .finally(() => {
          this.editor.instances.loading = false;
        });
    },
    formatInstanceDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
    // ── Definition download / load / revert (JSON preview tab) ──
    /** Save the CURRENT editor contents as `<workflow_key>.json`. */
    downloadDefinition() {
      const feedback = this.feedbackPayload();
      const intro = this.introMessagePayload();
      const def = {
        workflow_key: this.editor.workflow_key || 'workflow',
        name: this.editor.name || '',
        description: this.editor.description || '',
        ...(intro ? { intro_message: intro } : {}),
        ...(feedback ? { feedback } : {}),
        ...(this.editor.mcpVisible ? {} : { mcp_visible: false }),
        ...this.buildDefinition(),
      };
      const blob = new Blob([JSON.stringify(def, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug(this.editor.name) || this.editor.workflow_key || 'workflow'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    loadDefinitionFile(event) {
      const file = event.target && event.target.files ? event.target.files[0] : null;
      // Reset so picking the same file again re-fires change.
      event.target.value = '';
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          this.applyDefinitionJson(String(reader.result));
        } catch (e) {
          this.toast(`Could not load definition: ${e.message}`, true);
        }
      };
      reader.onerror = () => this.toast('Could not read the file.', true);
      reader.readAsText(file);
    },
    /** Replace the editor contents from a definition JSON — the same
     * parse path as opening a saved workflow, so anything the builder
     * can save it can load. Nothing persists until the user saves. */
    applyDefinitionJson(text) {
      const def = JSON.parse(text);
      if (!def || !Array.isArray(def.steps) || def.steps.length === 0) {
        throw new Error('the file has no steps array');
      }
      this.editor.steps = orderStepsLogically(this.modelFromDefinition(def));
      this.editor.feedback = this.feedbackFromDefinition(def.feedback);
      this.editor.mcpVisible = def.mcp_visible !== false;
      this.editor.introMessage = typeof def.intro_message === 'string' ? def.intro_message : '';
      if (typeof def.name === 'string' && def.name) this.editor.name = def.name;
      if (typeof def.description === 'string') this.editor.description = def.description;
      // The key is the workflow's IDENTITY — an existing workflow keeps
      // its own; a new one adopts the file's.
      if (this.editor.isNew && typeof def.workflow_key === 'string' && def.workflow_key) {
        this.editor.workflow_key = def.workflow_key;
      }
      this.editor.validation = null;
      this.editor.message = '';
      this.editor.openStep = 0;
      this.toast('Definition loaded into the editor — review and save.');
    },
    /** Discard unsaved editor changes: re-fetch the stored definition. */
    revertToSaved() {
      if (!this.editor.workflow_id) return;
      axios.get(`agent/workflows/admin/${this.editor.workflow_id}`).then((r) => {
        if (r.data.Status !== 'OK') {
          this.toast(r.data.Message || 'Could not reload the workflow', true);
          return;
        }
        const wf = r.data.Data;
        this.editor.steps = orderStepsLogically(this.modelFromDefinition(wf));
        this.editor.feedback = this.feedbackFromDefinition(wf.feedback);
        this.editor.mcpVisible = wf.mcp_visible !== false;
        this.editor.introMessage = typeof wf.intro_message === 'string' ? wf.intro_message : '';
        this.editor.name = wf.name;
        this.editor.description = wf.description || '';
        this.editor.validation = null;
        this.editor.message = '';
        this.editor.openStep = 0;
        this.toast('Reverted to the saved definition.');
      });
    },
    /** Compact start-provenance label: route, page context, and (when
     * not the portal) the surface. */
    contextLabel(ctx) {
      if (!ctx) return '—';
      const bits = [];
      if (ctx.route) bits.push(ctx.route);
      if (ctx.kind) bits.push(ctx.ref ? `${ctx.kind}: ${ctx.ref}` : ctx.kind);
      if (ctx.surface && ctx.surface !== 'portal') bits.push(ctx.surface);
      return bits.join(' · ') || '—';
    },
    /** Editor model for the workflow-level feedback widget config. */
    feedbackFromDefinition(fb) {
      return fb && fb.label
        ? {
            enabled: true,
            label: fb.label,
            count: Number(fb.count) || 5,
            pre: fb.pre_label || '',
            post: fb.post_label || '',
            triggerMax: Number(fb.text_trigger_max) || 0,
            prompt: fb.text_prompt || '',
          }
        : {
            enabled: false,
            label: 'How easy was it to get this done?',
            count: 5,
            pre: 'Difficult',
            post: 'Easy',
            triggerMax: 0,
            prompt: '',
          };
    },
    /** The definition/save shape for the intro message (undefined = the
     * standard greeting). */
    introMessagePayload() {
      const text = String(this.editor.introMessage || '').trim();
      return text || undefined;
    },
    /** The definition/save shape for the feedback config (undefined = off). */
    feedbackPayload() {
      const f = this.editor.feedback;
      if (!f || !f.enabled || !String(f.label || '').trim()) return undefined;
      const triggerMax = Math.min(Number(f.triggerMax) || 0, Number(f.count) || 5);
      return {
        label: String(f.label).trim(),
        count: Number(f.count) || 5,
        ...(String(f.pre || '').trim() ? { pre_label: String(f.pre).trim() } : {}),
        ...(String(f.post || '').trim() ? { post_label: String(f.post).trim() } : {}),
        ...(triggerMax > 0 ? { text_trigger_max: triggerMax } : {}),
        ...(triggerMax > 0 && String(f.prompt || '').trim()
          ? { text_prompt: String(f.prompt).trim() }
          : {}),
      };
    },

    // ── builder model helpers ─────────────────────────────────
    stepIcon(kind) {
      return {
        choice: 'mdi-format-list-bulleted',
        job_search: 'mdi-magnify',
        doc_list: 'mdi-file-document-multiple-outline',
        doc_view: 'mdi-file-document-outline',
        doc_upload: 'mdi-file-upload-outline',
        form: 'mdi-form-select',
        terminal: 'mdi-flag-checkered',
      }[kind];
    },
    kindLabel(kind) {
      return (this.stepKinds.find((k) => k.value === kind) || {}).text || kind;
    },
    targetSteps(fromStep) {
      return this.editor.steps
        .filter((s) => s.uid !== fromStep.uid)
        .map((s) => {
          const label = this.stepLabelFor(s.step_key);
          const kind = this.kindLabel(s.kind);
          // A prompt-derived label still carries its kind for context.
          return { text: label === kind ? label : `${label} (${kind})`, value: s.step_key };
        });
    },
    stepLabelFor(key) {
      return this.stepLabelByKey[key] || key || '(unnamed)';
    },
    uniqueStepKey(base) {
      let key = base;
      let n = 2;
      while (this.editor.steps.some((s) => s.step_key === key)) key = `${base}_${n++}`;
      return key;
    },
    addStep(kind) {
      const base = {
        choice: 'choose',
        job_search: 'find_job',
        doc_list: 'pick_doc',
        doc_view: 'view_doc',
        doc_upload: 'upload_doc',
        form: 'details',
        terminal: 'done',
      }[kind];
      const step = blankStep(kind, this.uniqueStepKey(base));
      if (kind === 'choice') this.addOption(step);
      this.editor.steps.push(step);
      this.editor.openStep = this.editor.steps.length - 1;
      this.editor.validation = null;
      return step;
    },
    /** Flow frame's Add — new unlinked step, staying on the Flow tab. */
    addStepInFlow(kind) {
      const step = this.addStep(kind);
      this.toast(`Added a ${this.kindLabel(step.kind)} step — use a box's ⋯ menu or the Steps tab to link it.`);
    },
    /**
     * Node ⋯ → Add: create the step AND link it from the source step's
     * next free outcome (a choice grows a new option when all its
     * options are already linked; single-outcome steps only link when
     * their outcome is free).
     */
    addLinkedStep(fromKey, kind) {
      const from = this.editor.steps.find((s) => s.step_key === fromKey);
      const step = this.addStep(kind);
      if (!from) return;
      const linked = this.linkFromStep(from, step.step_key);
      this.toast(
        linked
          ? `Added a ${this.kindLabel(step.kind)} step, linked from "${this.stepLabelFor(from.step_key)}".`
          : `Added a ${this.kindLabel(step.kind)} step — "${this.stepLabelFor(from.step_key)}"'s outcome is already linked, connect it manually.`,
      );
    },
    /** Wire `from`'s next free outgoing outcome to `toKey`. */
    linkFromStep(from, toKey) {
      switch (from.kind) {
        case 'choice': {
          const free = from.options.find((o) => !o.to);
          if (free) {
            free.to = toKey;
            return true;
          }
          from.options.push({
            key: '',
            label: `Option ${from.options.length + 1}`,
            description: '',
            to: toKey,
          });
          return true;
        }
        case 'job_search':
        case 'doc_list':
          if (!from.selectedTo) { from.selectedTo = toKey; return true; }
          return false;
        case 'form':
          if (!from.submittedTo) { from.submittedTo = toKey; return true; }
          return false;
        case 'doc_view':
          if (!from.confirmedTo) { from.confirmedTo = toKey; return true; }
          return false;
        case 'doc_upload':
          if (!from.createdTo) { from.createdTo = toKey; return true; }
          return false;
        default:
          return false; // terminal — no outgoing links
      }
    },
    /** Node ⋯ → Delete (removeStep handles link cleanup + validation reset). */
    deleteStepByKey(key) {
      const index = this.editor.steps.findIndex((s) => s.step_key === key);
      if (index !== -1) this.removeStep(index);
    },
    /** Open the node ⋯ menu at the click position. */
    openNodeMenu(event, key) {
      event.preventDefault();
      this.flowMenu.show = false;
      this.flowMenu.x = event.clientX;
      this.flowMenu.y = event.clientY;
      this.flowMenu.stepKey = key;
      this.$nextTick(() => {
        this.flowMenu.show = true;
      });
    },
    removeStep(si) {
      const removed = this.editor.steps[si];
      this.editor.steps.splice(si, 1);
      // Clear links that pointed at it — validation will flag the gaps.
      for (const step of this.editor.steps) {
        if (step.selectedTo === removed.step_key) step.selectedTo = null;
        if (step.submittedTo === removed.step_key) step.submittedTo = null;
        if (step.confirmedTo === removed.step_key) step.confirmedTo = null;
        if (step.createdTo === removed.step_key) step.createdTo = null;
        if (step.docViewSourceStep === removed.step_key) step.docViewSourceStep = null;
        for (const opt of step.options) if (opt.to === removed.step_key) opt.to = null;
      }
      this.editor.validation = null;
    },
    moveStep(si, dir) {
      const target = si + dir;
      const [step] = this.editor.steps.splice(si, 1);
      this.editor.steps.splice(target, 0, step);
      this.editor.openStep = target;
    },
    /** Reorder a row within any config list (form fields, search fields).
     * splice keeps Vue 2 reactivity intact; order = rendered order. */
    moveRow(list, index, dir) {
      const target = index + dir;
      if (target < 0 || target >= list.length) return;
      const [row] = list.splice(index, 1);
      list.splice(target, 0, row);
    },
    openStepByKey(key) {
      const index = this.editor.steps.findIndex((s) => s.step_key === key);
      if (index === -1) return;
      this.editor.tab = 0;
      this.editor.openStep = index;
    },
    setStart(step, value) {
      for (const s of this.editor.steps) s.is_start = false;
      if (value) step.is_start = true;
    },
    addOption(step) {
      step.options.push({ key: '', label: '', description: '', to: null });
    },
    addTemplateAttr(step) {
      step.templateAttrs.push({
        tpa_id: null,
        attr: null,
        part: null,
        htLevel: null,
        label: '',
        type: null,
        required: false,
        placeholder: '',
      });
    },
    addSearchField(step) {
      step.searchFields.push({
        attr: null,
        label: '',
        placeholder: '',
        required: false,
        valuesMode: 'none',
        optionsText: '',
        vfTemplate: '',
        vfTpaId: null,
        vfAttr: null,
        hvHtId: null,
        hvLevel: null,
        forceSelect: false,
        pills: false,
        allowTyping: false,
      });
    },
    addStaticField(step) {
      step.staticFields.push({
        key: '',
        label: '',
        type: 'text',
        optionsText: '',
        required: false,
        placeholder: '',
      });
    },

    // ── model ⇄ definition ────────────────────────────────────
    buildDefinition() {
      const steps = [];
      const connections = [];
      for (const step of this.editor.steps) {
        // Permission scope derives from the step's own template context:
        // a job_search's doc-type restriction, a template-driven form's
        // chosen template. No separate input — one field, one meaning.
        const requiresTmplNames =
          step.kind === 'job_search'
            ? step.searchDocTypes
            : step.kind === 'form' && step.fieldsMode === 'template' && step.templateName
              ? [step.templateName]
              : [];
        const requires = step.requiresAction
          ? {
              action: step.requiresAction,
              ...(requiresTmplNames.length ? { tmpl_names: [...requiresTmplNames] } : {}),
            }
          : undefined;
        if (step.kind === 'terminal') {
          steps.push({
            step_key: step.step_key,
            step_type: 'terminal',
            guidance: step.guidance || undefined,
            requires,
          });
          continue;
        }

        let props = { ...(step._extraProps || {}) };
        props.prompt = step.prompt;
        if (step.kind === 'choice') {
          const usedKeys = new Set();
          props.options = step.options.map((opt) => {
            // Keys are generated from labels once and then kept stable —
            // they identify the connection trigger, not the display text.
            let key = opt.key || slug(opt.label);
            while (usedKeys.has(key)) key = `${key}_2`;
            usedKeys.add(key);
            opt.key = key;
            return {
              key,
              label: opt.label,
              ...(opt.description ? { description: opt.description } : {}),
            };
          });
          for (const opt of step.options) {
            if (opt.to)
              connections.push({ from_step_key: step.step_key, trigger_key: opt.key, to_step_key: opt.to });
          }
        }
        if (step.kind === 'job_search') {
          if (step.placeholder) props.placeholder = step.placeholder;
          if (step.searchDocTypes.length) props.document_types = [...step.searchDocTypes];
          else delete props.document_types;
          const searchFields = step.searchFields
            .filter((sf) => sf.attr)
            .map((sf) => {
              const out = {
                attr: sf.attr,
                ...(sf.label ? { label: sf.label } : {}),
                ...(sf.placeholder ? { placeholder: sf.placeholder } : {}),
                ...(sf.required ? { required: true } : {}),
              };
              if (sf.valuesMode === 'static') {
                const options = (sf.optionsText || '').split(',').map((o) => o.trim()).filter(Boolean);
                if (options.length) {
                  out.options = options;
                  out.type = 'select';
                }
              } else if (sf.valuesMode === 'template' && sf.vfTemplate && (sf.vfTpaId || sf.vfAttr)) {
                out.values_from = {
                  tmpl_name: sf.vfTemplate,
                  ...(sf.vfTpaId ? { tpa_id: sf.vfTpaId } : { attr: sf.vfAttr }),
                };
                if (sf.forceSelect) out.type = 'select';
              } else if (sf.valuesMode === 'hierarchy' && sf.hvHtId && sf.hvLevel) {
                out.hierarchy_values = { ht_id: sf.hvHtId, level: sf.hvLevel };
                if (sf.forceSelect) out.type = 'select';
              }
              // Pills only means something on an options-shaped field; the
              // engine re-checks the ≤5 cap against the resolved values.
              if (sf.pills && (out.options || out.values_from || out.hierarchy_values)) {
                out.variant = 'pills';
              }
              // Free entry rides on the tree control, so it only applies to
              // a tree-capable source that wasn't flattened to a dropdown.
              if (sf.allowTyping && out.type !== 'select' && (out.values_from || out.hierarchy_values)) {
                out.free_text = true;
              }
              return out;
            });
          if (searchFields.length) props.search_fields = searchFields;
          else delete props.search_fields;
          if (step.searchHideKeyword && searchFields.length) props.hide_keyword = true;
          else delete props.hide_keyword;
          if (step.searchResultsMode === 'next_step') props.results = 'next_step';
          else delete props.results;
          if (step.selectedTo)
            connections.push({
              from_step_key: step.step_key,
              trigger_key: step.searchResultsMode === 'next_step' ? 'searched' : 'selected',
              to_step_key: step.selectedTo,
            });
        }
        if (step.kind === 'doc_list') {
          if (step.docListMode === 'search') {
            const searchFrom = {};
            if (step.docListSearchStep) searchFrom.step = step.docListSearchStep;
            if (step.docListLimit) searchFrom.limit = Number(step.docListLimit);
            props.search_from = searchFrom;
            delete props.source;
          } else {
            const source = {};
            if (step.docListQuery) source.query = step.docListQuery;
            if (step.docListTypes.length) source.document_types = [...step.docListTypes];
            if (step.docListStatuses.length) source.statuses = [...step.docListStatuses];
            if (step.docListLimit) source.limit = Number(step.docListLimit);
            if (Object.keys(source).length) props.source = source;
            else delete props.source;
            delete props.search_from;
          }
          if (step.docListOpenLabel) props.open_label = step.docListOpenLabel;
          else delete props.open_label;
          delete props.items; // engine-resolved, never authored
          if (step.selectedTo)
            connections.push({ from_step_key: step.step_key, trigger_key: 'selected', to_step_key: step.selectedTo });
        }
        if (step.kind === 'doc_view') {
          if (!step.prompt) delete props.prompt; // optional for doc_view
          if (step.docViewSourceStep) props.source_step = step.docViewSourceStep;
          else delete props.source_step;
          if (step.confirmLabel) props.confirm_label = step.confirmLabel;
          else delete props.confirm_label;
          delete props.document; // engine-resolved, never authored
          if (step.confirmedTo)
            connections.push({ from_step_key: step.step_key, trigger_key: 'confirmed', to_step_key: step.confirmedTo });
        }
        if (step.kind === 'doc_upload') {
          props.tmpl_name = step.uploadTmplName;
          if (step.uploadMode === 'extract') {
            // Chain-free extraction — mutually exclusive with chain_id/document_input.
            props.extract = {
              parts: (step.uploadExtractParts || [])
                .filter((gp) => gp.tp_id || gp.part)
                .map((gp) => ({
                  ...(gp.tp_id ? { tp_id: gp.tp_id } : { part: gp.part }),
                  ...(gp.instructions && gp.instructions.trim()
                    ? { instructions: gp.instructions.trim() }
                    : {}),
                  ...(gp.format ? { format: gp.format } : {}),
                  ...(gp.minItems > 0 && gp.format !== 'text' ? { min_items: gp.minItems } : {}),
                  ...(gp.maxItems > 0 && gp.format !== 'text' ? { max_items: gp.maxItems } : {}),
                  ...(gp.childMin > 0 && gp.format !== 'text' ? { child_min_items: gp.childMin } : {}),
                  ...(gp.childMax > 0 && gp.format !== 'text' ? { child_max_items: gp.childMax } : {}),
                })),
              ...(step.uploadExtractContext && step.uploadExtractContext.trim()
                ? { context: step.uploadExtractContext.trim() }
                : {}),
            };
            delete props.chain_id;
            delete props.document_input;
          } else {
            delete props.extract;
            if (step.uploadChainId) props.chain_id = step.uploadChainId;
            else delete props.chain_id;
            if (step.uploadDocumentInput) props.document_input = step.uploadDocumentInput;
            else delete props.document_input;
          }
          const accept = (step.uploadAccept || '')
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean);
          if (accept.length) props.accept = accept;
          else delete props.accept;
          if (step.uploadConfirmLabel) props.confirm_label = step.uploadConfirmLabel;
          else delete props.confirm_label;
          // engine-emitted phase payloads, never authored
          delete props.phase;
          delete props.review;
          if (step.createdTo)
            connections.push({ from_step_key: step.step_key, trigger_key: 'created', to_step_key: step.createdTo });
        }
        if (step.kind === 'form') {
          if (step.submitLabel) props.submit_label = step.submitLabel;
          if (step.fieldsMode === 'template') {
            props.template_fields = {
              tmpl_name: step.templateName,
              attrs: step.templateAttrs
                .filter((fa) => fa.tpa_id || fa.attr)
                .map((fa) => {
                  const htId = this.formFieldHtId(fa);
                  const control = splitFieldControl(fa.type);
                  const shared = {
                    ...(fa.label ? { label: fa.label } : {}),
                    ...(control.type ? { type: control.type } : {}),
                    ...(control.pills ? { variant: 'pills' } : {}),
                    ...(control.free ? { free_text: true } : {}),
                    ...(fa.required ? { required: true } : {}),
                  };
                  if (htId) {
                    return {
                      hierarchy_values: { ht_id: htId, level: fa.htLevel || 1 },
                      ...shared,
                    };
                  }
                  return {
                    ...(fa.tpa_id ? { tpa_id: fa.tpa_id } : { attr: fa.attr }),
                    ...(fa.part && !fa.tpa_id ? { part: fa.part } : {}),
                    ...shared,
                    ...(fa.placeholder ? { placeholder: fa.placeholder } : {}),
                  };
                }),
            };
            delete props.fields;
            // On-submit create behaviour. Only meaningful with template
            // fields (the template binding is what gets created).
            const createOpts = {
              ...(step.titleTriage ? { title_triage: true } : {}),
              ...(step.createMissingNode ? { create_missing_node: true } : {}),
            };
            if (step.createMode === 'chain' || step.createMode === 'user_choice') {
              props.create_document = {
                chain_id: step.createChainId || undefined,
                ...(step.createMode === 'user_choice' ? { user_choice: true } : {}),
                ...createOpts,
              };
            } else if (this.isGenerateMode(step)) {
              const post = [
                ...(step.generatePostSkills ? ['skills_extract'] : []),
                ...(step.generatePostProficiency ? ['skill_proficiency'] : []),
              ].map((capability) =>
                step.generateSkillsPart ? { capability, part: step.generateSkillsPart } : capability,
              );
              props.create_document = {
                generate: {
                  parts: (step.generateParts || [])
                    .filter((gp) => gp.tp_id || gp.part)
                    .map((gp) => ({
                      ...(gp.tp_id ? { tp_id: gp.tp_id } : { part: gp.part }),
                      ...(gp.instructions && gp.instructions.trim()
                        ? { instructions: gp.instructions.trim() }
                        : {}),
                      ...(gp.format ? { format: gp.format } : {}),
                      ...(gp.minItems > 0 && gp.format !== 'text' ? { min_items: gp.minItems } : {}),
                      ...(gp.maxItems > 0 && gp.format !== 'text' ? { max_items: gp.maxItems } : {}),
                      ...(gp.childMin > 0 && gp.format !== 'text' ? { child_min_items: gp.childMin } : {}),
                      ...(gp.childMax > 0 && gp.format !== 'text' ? { child_max_items: gp.childMax } : {}),
                    })),
                  ...(step.generateContext && step.generateContext.trim()
                    ? { context: step.generateContext.trim() }
                    : {}),
                  ...(post.length ? { post } : {}),
                },
                ...(step.createMode === 'generate_user_choice' ? { user_choice: true } : {}),
                ...createOpts,
              };
            } else if (step.createMode === 'direct') {
              props.create_document = createOpts;
            } else {
              delete props.create_document;
            }
          } else {
            const usedKeys = new Set();
            props.fields = step.staticFields
              .filter((f) => f.label)
              .map((f) => {
                let key = f.key || slug(f.label);
                while (usedKeys.has(key)) key = `${key}_2`;
                usedKeys.add(key);
                f.key = key;
                const control = splitFieldControl(f.type);
                const isOptionType = ['select', 'radio', 'checkbox'].includes(control.type);
                return {
                  key,
                  label: f.label,
                  type: control.type,
                  ...(control.pills ? { variant: 'pills' } : {}),
                  ...(isOptionType
                    ? { options: (f.optionsText || '').split(',').map((o) => o.trim()).filter(Boolean) }
                    : {}),
                  ...(f.required ? { required: true } : {}),
                  ...(!isOptionType && f.placeholder ? { placeholder: f.placeholder } : {}),
                };
              });
            delete props.template_fields;
            delete props.create_document;
          }
          if (step.submittedTo)
            connections.push({ from_step_key: step.step_key, trigger_key: 'submitted', to_step_key: step.submittedTo });
        }

        steps.push({
          step_key: step.step_key,
          step_type: 'ui_block',
          block_type: step.kind,
          props,
          guidance: step.guidance || undefined,
          requires,
          is_start: step.is_start || undefined,
          ...(step.isEntry
            ? { is_entry: true, entry_hint: (step.entryHint || '').trim() || undefined }
            : {}),
        });
      }
      return { steps, connections };
    },
    modelFromDefinition(wf) {
      const connections = wf.connections || [];
      const outgoing = (key) => connections.filter((c) => c.from_step_key === key);
      return (wf.steps || []).map((s) => {
        const kind = s.step_type === 'terminal' ? 'terminal' : s.block_type;
        const step = blankStep(kind, s.step_key);
        step.is_start = s.is_start === true;
        step.isEntry = s.is_entry === true;
        step.entryHint = s.entry_hint || '';
        step.guidance = s.guidance || '';
        if (s.requires) {
          step.requiresAction = s.requires.action || null;
          step.requiresTmplNames = s.requires.tmpl_names || [];
        }
        const props = s.props || {};
        step.prompt = props.prompt || '';
        if (kind === 'choice') {
          step.options = (props.options || []).map((o) => ({
            key: o.key,
            label: o.label,
            description: o.description || '',
            to: (outgoing(s.step_key).find((c) => c.trigger_key === o.key) || {}).to_step_key || null,
          }));
        }
        if (kind === 'job_search') {
          step.placeholder = props.placeholder || '';
          step.searchDocTypes = props.document_types || [];
          step.searchFields = (props.search_fields || []).map((sf) => ({
            attr: sf.attr,
            label: sf.label || '',
            placeholder: sf.placeholder || '',
            required: sf.required === true,
            valuesMode: sf.hierarchy_values
              ? 'hierarchy'
              : sf.values_from
                ? 'template'
                : Array.isArray(sf.options) && sf.options.length
                  ? 'static'
                  : 'none',
            optionsText: Array.isArray(sf.options) ? sf.options.join(', ') : '',
            vfTemplate: sf.values_from ? sf.values_from.tmpl_name || '' : '',
            vfTpaId: sf.values_from ? sf.values_from.tpa_id || this.resolveTpaId(sf.values_from.tmpl_name, sf.values_from.attr, sf.values_from.part) : null,
            vfAttr: sf.values_from ? sf.values_from.attr || null : null,
            hvHtId: sf.hierarchy_values ? sf.hierarchy_values.ht_id : null,
            hvLevel: sf.hierarchy_values ? sf.hierarchy_values.level : null,
            forceSelect: sf.type === 'select' && !!(sf.values_from || sf.hierarchy_values),
            pills: sf.variant === 'pills',
            allowTyping: sf.free_text === true,
          }));
          step.searchHideKeyword = props.hide_keyword === true;
          step.searchResultsMode = props.results === 'next_step' ? 'next_step' : 'inline';
          // The outgoing link is trigger 'selected' (inline results) or
          // 'searched' (pass-to-next-step) — accept either on load.
          step.selectedTo =
            (
              outgoing(s.step_key).find(
                (c) => c.trigger_key === 'selected' || c.trigger_key === 'searched',
              ) || {}
            ).to_step_key || null;
        }
        if (kind === 'doc_list') {
          const source = props.source || {};
          step.docListMode = props.search_from ? 'search' : 'query';
          step.docListSearchStep = props.search_from ? props.search_from.step || null : null;
          step.docListQuery = source.query || '';
          step.docListTypes = source.document_types || [];
          step.docListStatuses = source.statuses || [];
          step.docListLimit = props.search_from
            ? props.search_from.limit || null
            : source.limit || null;
          step.docListOpenLabel = props.open_label || '';
          step.selectedTo =
            (outgoing(s.step_key).find((c) => c.trigger_key === 'selected') || {}).to_step_key || null;
        }
        if (kind === 'doc_view') {
          step.docViewSourceStep = props.source_step || null;
          step.confirmLabel = props.confirm_label || '';
          step.confirmedTo =
            (outgoing(s.step_key).find((c) => c.trigger_key === 'confirmed') || {}).to_step_key || null;
        }
        if (kind === 'doc_upload') {
          step.uploadTmplName = props.tmpl_name || '';
          // Legacy steps carry chain_id; new ones carry extract. Default new/empty to extract.
          step.uploadMode = props.chain_id && !props.extract ? 'chain' : 'extract';
          if (props.extract) {
            step.uploadExtractParts = (props.extract.parts || []).map((gp) => ({
              tp_id: gp.tp_id || this.resolveTpIdByPartName(step.uploadTmplName, gp.part),
              part: gp.part || null,
              instructions: gp.instructions || '',
              format: gp.format || '',
              minItems: gp.min_items || null,
              maxItems: gp.max_items || null,
              childMin: gp.child_min_items || null,
              childMax: gp.child_max_items || null,
            }));
            step.uploadExtractContext = props.extract.context || '';
          }
          step.uploadChainId = props.chain_id || null;
          step.uploadDocumentInput = props.document_input || null;
          step.uploadAccept = Array.isArray(props.accept) ? props.accept.join(', ') : '';
          step.uploadConfirmLabel = props.confirm_label || '';
          step.createdTo =
            (outgoing(s.step_key).find((c) => c.trigger_key === 'created') || {}).to_step_key || null;
        }
        if (kind === 'form') {
          step.submitLabel = props.submit_label || '';
          step.submittedTo =
            (outgoing(s.step_key).find((c) => c.trigger_key === 'submitted') || {}).to_step_key || null;
          if (props.template_fields) {
            step.fieldsMode = 'template';
            step.templateName = props.template_fields.tmpl_name || '';
            step.templateAttrs = (props.template_fields.attrs || []).map((a) => ({
              // Hierarchy fields ride the same picker model under an
              // encoded 'ht:<id>' value — see formFieldHtId.
              tpa_id: a.hierarchy_values
                ? `ht:${a.hierarchy_values.ht_id}`
                : a.tpa_id || this.resolveTpaId(step.templateName, a.attr, a.part),
              attr: a.attr || null,
              part: a.part || null,
              htLevel: a.hierarchy_values ? a.hierarchy_values.level || null : null,
              label: a.label || '',
              type: joinFieldControl(a.type, a.variant, a.free_text === true),
              required: a.required === true,
              placeholder: a.placeholder || '',
            }));
            const cd = props.create_document;
            step.createMode = cd
              ? cd.chain_id
                ? cd.user_choice === true
                  ? 'user_choice'
                  : 'chain'
                : cd.generate
                  ? cd.user_choice === true
                    ? 'generate_user_choice'
                    : 'generate'
                  : 'direct'
              : 'none';
            step.createChainId = cd?.chain_id || null;
            step.titleTriage = cd?.title_triage === true;
            step.createMissingNode = cd?.create_missing_node === true;
            if (cd?.generate) {
              step.generateParts = (cd.generate.parts || []).map((gp) => ({
                tp_id: gp.tp_id || this.resolveTpIdByPartName(step.templateName, gp.part),
                part: gp.part || null,
                instructions: gp.instructions || '',
                format: gp.format || '',
                minItems: gp.min_items || null,
                maxItems: gp.max_items || null,
                childMin: gp.child_min_items || null,
                childMax: gp.child_max_items || null,
              }));
              step.generateContext = cd.generate.context || '';
              const post = Array.isArray(cd.generate.post) ? cd.generate.post : [];
              const capOf = (e) => (typeof e === 'string' ? e : e && e.capability);
              step.generatePostSkills = post.some((e) => capOf(e) === 'skills_extract');
              step.generatePostProficiency = post.some((e) => capOf(e) === 'skill_proficiency');
              const withPart = post.find((e) => e && typeof e === 'object' && e.part);
              step.generateSkillsPart = withPart ? withPart.part : null;
            }
          } else {
            step.fieldsMode = 'static';
            step.staticFields = (props.fields || []).map((f) => ({
              key: f.key,
              label: f.label,
              type: joinFieldControl(f.type, f.variant) || 'text',
              optionsText: Array.isArray(f.options) ? f.options.join(', ') : '',
              required: f.required === true,
              placeholder: f.placeholder || '',
            }));
          }
        }
        // Preserve props this builder doesn't model (forward compat).
        const known = [
          'prompt', 'options', 'placeholder', 'submit_label', 'fields', 'template_fields',
          'source', 'items', 'source_step', 'confirm_label', 'document',
          'document_types', 'search_fields', 'hide_keyword', 'results', 'search_from',
          'open_label', 'create_document',
          'tmpl_name', 'chain_id', 'document_input', 'extract', 'accept', 'phase', 'review',
        ];
        step._extraProps = Object.fromEntries(
          Object.entries(props).filter(([k]) => !known.includes(k)),
        );
        return step;
      });
    },
    /**
     * Mirror the ENGINE's template-name resolution (case-insensitive
     * exact match wins; else a contains match only when unique) so
     * editor validation predicts exactly what runtime will do.
     */
    resolveTemplateByName(name) {
      const needle = `${name || ''}`.trim().toLowerCase();
      if (!needle) return null;
      const exact = this.templates.filter((t) => t.tmpl_name.trim().toLowerCase() === needle);
      if (exact.length === 1) return exact[0];
      if (exact.length > 1) return null;
      const contains = this.templates.filter((t) => t.tmpl_name.toLowerCase().includes(needle));
      return contains.length === 1 ? contains[0] : null;
    },
    /** Mirror the engine's attr-name resolution (exact → unique contains,
     * optionally narrowed by a part-name contains filter). */
    resolveAttrByName(tmpl, attr, part) {
      if (!tmpl || !attr) return null;
      const needle = `${attr}`.trim().toLowerCase();
      if (!needle) return null;
      const candidates = part
        ? tmpl.attrs.filter((a) => a.part.toLowerCase().includes(`${part}`.trim().toLowerCase()))
        : tmpl.attrs;
      const exact = candidates.filter((a) => a.title.trim().toLowerCase() === needle);
      if (exact.length === 1) return exact[0];
      if (exact.length > 1) return null;
      const contains = candidates.filter((a) => a.title.toLowerCase().includes(needle));
      return contains.length === 1 ? contains[0] : null;
    },
    /** Best-effort: adopt the tpa_id for a name-based attr so the picker shows it. */
    resolveTpaId(templateName, attr, part) {
      const match = this.resolveAttrByName(this.resolveTemplateByName(templateName), attr, part);
      return match ? match.tpa_id : null;
    },
    /** Mirror the generation service's part-name resolution (exact →
     * unique contains over top-level parts) so a name-based generate
     * section adopts its tp_id for the picker. */
    resolveTpIdByPartName(templateName, partName) {
      const tmpl = this.resolveTemplateByName(templateName);
      const needle = `${partName || ''}`.trim().toLowerCase();
      if (!tmpl || !tmpl.parts || !needle) return null;
      const topLevel = tmpl.parts.filter((p) => p.parent_tp_id === null);
      const exact = topLevel.filter((p) => p.tp_name.trim().toLowerCase() === needle);
      if (exact.length === 1) return exact[0].tp_id;
      if (exact.length > 1) return null;
      const contains = topLevel.filter((p) => p.tp_name.toLowerCase().includes(needle));
      return contains.length === 1 ? contains[0].tp_id : null;
    },
    /**
     * Authoring-time checks the server-side validator can't make — they
     * need this client's template/hierarchy reference data. Every
     * template-fields row is resolved the way the ENGINE resolves it at
     * emit time, so a config that would silently drop fields (or, with
     * nothing resolving and no fallback, kill the step for the user) is
     * flagged before it's saved.
     */
    localValidationErrors() {
      const errors = [];
      for (const step of this.editor.steps) {
        if (step.kind === 'doc_upload') {
          const uLabel = `upload step "${this.stepLabelFor(step.step_key)}"`;
          if (!step.uploadTmplName) {
            errors.push(`${uLabel}: pick the target template`);
          } else if (!this.resolveTemplateByName(step.uploadTmplName)) {
            errors.push(`${uLabel}: template "${step.uploadTmplName}" does not resolve uniquely for this client`);
          }
          if (step.uploadMode === 'extract') {
            const rows = (step.uploadExtractParts || []).filter((gp) => gp.tp_id || gp.part);
            if (!rows.length) {
              errors.push(`${uLabel}: add at least one section to extract`);
            }
            const extractable = this.templatePartItems(step.uploadTmplName).map((i) => i.value);
            rows.forEach((gp, i) => {
              const rowLabel = `${uLabel} section ${i + 1}`;
              const tpId = gp.tp_id || this.resolveTpIdByPartName(step.uploadTmplName, gp.part);
              if (!tpId) {
                errors.push(
                  `${rowLabel}: "${gp.part}" does not match a section on template "${step.uploadTmplName}" — re-pick it from the list`,
                );
              } else if (!extractable.includes(tpId)) {
                errors.push(
                  `${rowLabel}: "${gp.part || tpId}" is not an extractable section (locked, tag, or decoration) — re-pick it from the list`,
                );
              }
              if (gp.minItems > 0 && gp.maxItems > 0 && gp.minItems > gp.maxItems) {
                errors.push(`${rowLabel}: min items is greater than max items`);
              }
              if (gp.childMin > 0 && gp.childMax > 0 && gp.childMin > gp.childMax) {
                errors.push(`${rowLabel}: child min is greater than child max`);
              }
            });
          } else {
            if (!step.uploadChainId) {
              errors.push(`${uLabel}: pick the content chain that processes the uploaded document`);
            } else if (!this.templateChainItems(step.uploadTmplName).some((c) => c.value === step.uploadChainId)) {
              errors.push(`${uLabel}: the selected chain is not linked to template "${step.uploadTmplName}" for this client`);
            }
            if (!step.uploadDocumentInput) {
              errors.push(`${uLabel}: pick which chain input receives the document text`);
            } else if (
              step.uploadChainId &&
              !this.chainInputItems(step.uploadChainId).includes(step.uploadDocumentInput)
            ) {
              errors.push(`${uLabel}: "${step.uploadDocumentInput}" is not an input of the selected chain`);
            }
          }
          continue;
        }
        if (step.kind !== 'form' || step.fieldsMode !== 'template') continue;
        const label = `create step "${this.stepLabelFor(step.step_key)}"`;
        if (!step.templateName) {
          errors.push(`${label}: no template selected`);
          continue;
        }
        const tmpl = this.resolveTemplateByName(step.templateName);
        if (!tmpl) {
          errors.push(
            `${label}: template "${step.templateName}" does not resolve uniquely for this client`,
          );
          continue;
        }
        const rows = step.templateAttrs.filter((fa) => fa.tpa_id || fa.attr);
        if (!rows.length) {
          errors.push(`${label}: no fields configured`);
          continue;
        }
        let resolvable = 0;
        rows.forEach((fa, i) => {
          const htId = this.formFieldHtId(fa);
          if (htId) {
            if (this.hierarchies.some((h) => h.hierarchy_type_id === htId)) resolvable++;
            else errors.push(`${label} field ${i + 1}: hierarchy type ${htId} does not exist for this client`);
            return;
          }
          if (typeof fa.tpa_id === 'number') {
            if (tmpl.attrs.some((a) => a.tpa_id === fa.tpa_id)) resolvable++;
            else errors.push(
              `${label} field ${i + 1}: attribute id ${fa.tpa_id} is not on template "${tmpl.tmpl_name}" — re-pick it from the list`,
            );
            return;
          }
          if (this.resolveAttrByName(tmpl, fa.attr, fa.part)) resolvable++;
          else errors.push(
            `${label} field ${i + 1}: attribute "${fa.attr}" does not match a unique attribute on template "${tmpl.tmpl_name}" for this client — pick it from the list`,
          );
        });
        if (!resolvable) {
          errors.push(
            `${label}: none of its fields resolve — at runtime this step would fail with "workflow not available"`,
          );
        }
        if (step.createMode === 'chain' || step.createMode === 'user_choice') {
          if (!step.createChainId) {
            errors.push(`${label}: pick the content chain to run on submit (or switch to direct create)`);
          } else if (!this.templateChainItems(step.templateName).some((c) => c.value === step.createChainId)) {
            errors.push(
              `${label}: the selected content chain is not linked to template "${step.templateName}" for this client`,
            );
          }
        }
        if (this.isGenerateMode(step)) {
          const sections = (step.generateParts || []).filter((gp) => gp.tp_id || gp.part);
          if (!sections.length) {
            errors.push(`${label}: add at least one section to draft (or switch the On-submit mode)`);
          }
          const topLevelParts = (tmpl.parts || []).filter((p) => p.parent_tp_id === null);
          sections.forEach((gp, i) => {
            if (gp.tp_id) {
              if (!topLevelParts.some((p) => p.tp_id === gp.tp_id)) {
                errors.push(
                  `${label} section ${i + 1}: part id ${gp.tp_id} is not on template "${tmpl.tmpl_name}" — re-pick it from the list`,
                );
              }
            } else if (!this.resolveTpIdByPartName(step.templateName, gp.part)) {
              errors.push(
                `${label} section ${i + 1}: "${gp.part}" does not match a unique part on template "${tmpl.tmpl_name}" — pick it from the list`,
              );
            }
            if (gp.minItems > 0 && gp.maxItems > 0 && gp.minItems > gp.maxItems) {
              errors.push(`${label} section ${i + 1}: min items cannot exceed max items`);
            }
            if (gp.childMin > 0 && gp.childMax > 0 && gp.childMin > gp.childMax) {
              errors.push(`${label} section ${i + 1}: min child rows cannot exceed max child rows`);
            }
          });
          if (
            (step.generatePostSkills || step.generatePostProficiency) &&
            !this.tagPartItems(step.templateName).length
          ) {
            errors.push(
              `${label}: the skills options need a tag part on template "${tmpl.tmpl_name}" — none exists, so they would be skipped at runtime`,
            );
          }
        }
      }
      return errors;
    },

    // ── open / save ───────────────────────────────────────────
    newWorkflow() {
      Object.assign(this.editor, {
        open: true,
        tab: 1,
        openStep: 0,
        isNew: true,
        fromGlobal: false,
        workflow_id: null,
        status: 'draft',
        workflow_key: '',
        name: '',
        description: '',
        introMessage: '',
        mcpVisible: true,
        feedback: this.feedbackFromDefinition(null),
        steps: [],
        validation: null,
        message: '',
        messageIsError: false,
        instances: { loading: false, loaded: false, rows: [] },
        copyReport: null,
        copySource: '',
      });
      // Starter shape: a choice with two end points.
      this.addStep('choice');
      this.addStep('terminal');
      this.addStep('terminal');
      const [choice, a, b] = this.editor.steps;
      choice.is_start = true;
      choice.prompt = 'What would you like to do?';
      choice.options = [
        { key: '', label: 'Option A', description: '', to: a.step_key },
        { key: '', label: 'Option B', description: '', to: b.step_key },
      ];
      this.editor.openStep = 0;
    },
    editWorkflow(item) {
      axios.get(`agent/workflows/admin/${item.workflow_id}`).then((r) => {
        if (r.data.Status !== 'OK') {
          this.toast(r.data.Message || 'Could not load workflow', true);
          return;
        }
        const wf = r.data.Data;
        Object.assign(this.editor, {
          open: true,
          tab: 1,
          openStep: 0,
          isNew: false,
          fromGlobal: wf.client_id === null,
          workflow_id: wf.workflow_id ?? item.workflow_id ?? null,
          status: wf.status || item.status || 'draft',
          workflow_key: wf.workflow_key,
          name: wf.name,
          description: wf.description || '',
          introMessage: typeof wf.intro_message === 'string' ? wf.intro_message : '',
          mcpVisible: wf.mcp_visible !== false,
          feedback: this.feedbackFromDefinition(wf.feedback),
          steps: orderStepsLogically(this.modelFromDefinition(wf)),
          validation: null,
          message: '',
          messageIsError: false,
          instances: { loading: false, loaded: false, rows: [] },
          copyReport: null,
          copySource: '',
        });
      });
    },
    openCopyDialog() {
      this.copyDialog.open = true;
      this.copyDialog.selected = null;
      this.copyDialog.loading = true;
      axios
        .get('agent/workflows/admin/copy-sources')
        .then((r) => {
          this.copyDialog.sources = r.data.Status === 'OK' ? r.data.Data || [] : [];
        })
        .finally(() => (this.copyDialog.loading = false));
    },
    doCopyPrepare() {
      this.copyDialog.preparing = true;
      axios
        .post('agent/workflows/admin/copy-prepare', {
          source_workflow_id: this.copyDialog.selected,
        })
        .then((r) => {
          if (r.data.Status !== 'OK') {
            this.toast(r.data.Message || 'Could not prepare the copy', true);
            return;
          }
          const { definition, report, source } = r.data.Data;
          // The save path UPSERTS by (key, client) — the copy gets its
          // own minted key so it can't overwrite an existing workflow
          // (never the source client's key).
          const key = this.mintWorkflowKey();
          this.copyDialog.open = false;
          Object.assign(this.editor, {
            open: true,
            tab: 1,
            openStep: 0,
            isNew: true,
            fromGlobal: false,
            workflow_id: null,
            status: 'draft',
            workflow_key: key,
            name: definition.name,
            description: definition.description || '',
            introMessage: typeof definition.intro_message === 'string' ? definition.intro_message : '',
            mcpVisible: definition.mcp_visible !== false,
            feedback: this.feedbackFromDefinition(definition.feedback),
            steps: orderStepsLogically(this.modelFromDefinition(definition)),
            validation: null,
            message: '',
            messageIsError: false,
            instances: { loading: false, loaded: false, rows: [] },
            copyReport: report || [],
            copySource: `${source.client_name} / ${source.name}`,
          });
        })
        .finally(() => (this.copyDialog.preparing = false));
    },
    validateDefinition() {
      const def = this.buildDefinition();
      const localErrors = this.localValidationErrors();
      axios
        .post('agent/workflows/admin/validate', {
          workflow_key: this.editor.workflow_key || 'unsaved',
          name: this.editor.name || 'unsaved',
          intro_message: this.introMessagePayload(),
          feedback: this.feedbackPayload(),
          steps: def.steps,
          connections: def.connections,
        })
        .then((r) => {
          if (r.data.Status === 'OK')
            this.editor.validation = [...localErrors, ...(r.data.Data.errors || [])];
        });
    },
    save(status) {
      const def = this.buildDefinition();
      // Reference-data checks run client-side (the server validator is
      // schema-only). Drafts may save with warnings; publishing a config
      // that can't resolve for this client is refused — that's exactly
      // the state that dead-ends the workflow for end users.
      const localErrors = this.localValidationErrors();
      if (localErrors.length && status === 'published') {
        this.editor.validation = localErrors;
        this.editor.message = 'Fix the issues below before publishing.';
        this.editor.messageIsError = true;
        return;
      }
      // First save of a new workflow: mint the internal wire identity
      // (no user-entered keys — workflows are known by name).
      if (!this.editor.workflow_key) this.editor.workflow_key = this.mintWorkflowKey();
      this.editor.saving = true;
      this.editor.message = '';
      axios
        .post('agent/workflows/admin/save', {
          workflow_key: this.editor.workflow_key,
          name: this.editor.name,
          description: this.editor.description,
          status,
          intro_message: this.introMessagePayload(),
          feedback: this.feedbackPayload(),
          mcp_visible: !!this.editor.mcpVisible,
          steps: def.steps,
          connections: def.connections,
        })
        .then((r) => {
          const data = r.data.Data || {};
          const serverErrors = data.errors || [];
          this.editor.validation =
            localErrors.length || serverErrors.length ? [...localErrors, ...serverErrors] : data.errors || null;
          if (r.data.Status === 'OK') {
            this.editor.message =
              status === 'published'
                ? 'Saved and published.'
                : this.editor.validation && this.editor.validation.length
                  ? 'Draft saved (with validation warnings).'
                  : 'Draft saved.';
            this.editor.messageIsError = false;
            this.editor.isNew = false;
            this.editor.fromGlobal = false;
            this.editor.status = status;
            this.loadAll();
          } else {
            this.editor.message = r.data.Message || 'Save failed';
            this.editor.messageIsError = true;
          }
        })
        .finally(() => (this.editor.saving = false));
    },
    /** Nominal wire identity (workflow_key) — minted, never user-entered
     * or shown. Timestamp base36 + random tail: unique against every
     * loaded row (the save endpoint upserts by key, so a collision
     * would overwrite), race-safe across concurrent admins, and always
     * matching the server's key rule. */
    mintWorkflowKey() {
      const taken = new Set((this.workflows || []).map((w) => w.workflow_key));
      let key;
      do {
        key = `wf-${Date.now().toString(36)}-${Math.floor(Math.random() * 46656)
          .toString(36)
          .padStart(3, '0')}`;
      } while (taken.has(key));
      return key;
    },
    openSaveAs() {
      this.saveAsDialog.name = this.editor.name ? `${this.editor.name} (copy)` : '';
      this.saveAsDialog.error = '';
      this.saveAsDialog.open = true;
    },
    doSaveAs() {
      const key = this.mintWorkflowKey();
      const def = this.buildDefinition();
      this.editor.saving = true;
      axios
        .post('agent/workflows/admin/save', {
          workflow_key: key,
          name: this.saveAsDialog.name || `${this.editor.name} (copy)`,
          description: this.editor.description,
          status: 'draft',
          intro_message: this.introMessagePayload(),
          feedback: this.feedbackPayload(),
          mcp_visible: !!this.editor.mcpVisible,
          steps: def.steps,
          connections: def.connections,
        })
        .then((r) => {
          if (r.data.Status !== 'OK') {
            this.saveAsDialog.error = r.data.Message || 'Could not save the copy';
            return;
          }
          const data = r.data.Data || {};
          this.saveAsDialog.open = false;
          // The editor now IS the copy — further edits land on it, and
          // the original stays as it was on disk.
          Object.assign(this.editor, {
            isNew: false,
            fromGlobal: false,
            workflow_id: data.workflow_id ?? null,
            status: 'draft',
            workflow_key: key,
            name: this.saveAsDialog.name || `${this.editor.name} (copy)`,
            validation: data.errors && data.errors.length ? data.errors : null,
            message: `Saved as new draft "${this.saveAsDialog.name || `${this.editor.name} (copy)`}" — you are now editing the copy.`,
            messageIsError: false,
            instances: { loading: false, loaded: false, rows: [] },
            copyReport: null,
            copySource: '',
          });
          this.toast('Saved as a new draft.');
          this.loadAll();
        })
        .finally(() => (this.editor.saving = false));
    },
    /** Editor-footer Deactivate: status-only (never saves the open
     * definition, so unsaved edits aren't silently published). */
    deactivateFromEditor() {
      axios
        .post(`agent/workflows/admin/${this.editor.workflow_id}/status`, { status: 'inactive' })
        .then((r) => {
          if (r.data.Status === 'OK') {
            this.editor.status = 'inactive';
            this.editor.message = 'Deactivated — hidden from users; Save & Publish reactivates it.';
            this.editor.messageIsError = false;
            this.loadAll();
          } else {
            this.editor.message = r.data.Message || 'Could not deactivate';
            this.editor.messageIsError = true;
          }
        });
    },
    setStatus(item, status) {
      axios.post(`agent/workflows/admin/${item.workflow_id}/status`, { status }).then((r) => {
        if (r.data.Status === 'OK') {
          this.toast(
            { published: 'Published.', draft: 'Set to draft.', inactive: 'Deactivated.' }[status] ||
              'Status changed.',
          );
          this.loadAll();
        } else {
          this.toast(r.data.Message || 'Could not change status', true);
        }
      });
    },
    confirmDelete(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.open = true;
    },
    doDelete() {
      const item = this.deleteDialog.item;
      this.deleteDialog.open = false;
      axios.delete(`agent/workflows/admin/${item.workflow_id}`).then((r) => {
        if (r.data.Status === 'OK') {
          this.toast('Deleted.');
          this.loadAll();
        } else {
          this.toast(r.data.Message || 'Could not delete', true);
        }
      });
    },
    toast(text, isError) {
      this.snack.text = text;
      this.snack.colour = isError ? 'error' : 'success';
      this.snack.open = true;
    },
  },
};
</script>

<style scoped>
.wf-table >>> tbody tr {
  cursor: pointer;
}
.wf-json >>> textarea {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.5;
}
/* Stats tab: executions-per-day mini bars. */
.wf-stats-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 64px;
  max-width: 640px;
  padding: 2px 0;
  border-bottom: 1px solid rgb(224 231 255);
}
.wf-stats-bar {
  flex: 1 1 0;
  display: flex;
  align-items: flex-end;
  height: 100%;
}
.wf-stats-bar__fill {
  width: 100%;
  border-radius: 2px 2px 0 0;
  background: rgb(129 140 248);
  min-height: 2px;
}
.wf-flow {
  position: relative;
}
.wf-flow__add {
  position: absolute;
  top: -2px;
  right: 4px;
  z-index: 2;
}
.wf-flow__help {
  position: absolute;
  top: 2px;
  right: 110px; /* sits just left of the Add step button */
  z-index: 2;
}
.wf-flow__node-menu {
  font-size: 15px;
  font-weight: 700;
  fill: #64748b;
  cursor: pointer;
}
.wf-flow__node-menu:hover {
  fill: #1e293b;
}
.wf-flow__menu-header {
  height: 28px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.wf-flow__scroll {
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}
.wf-flow__node {
  cursor: pointer;
}
.wf-flow__node:hover rect {
  filter: brightness(0.96);
}
.wf-flow__node-key {
  font-size: 13px;
  font-weight: 600;
  fill: #1f2937;
}
.wf-flow__node-kind {
  font-size: 11px;
  fill: #6b7280;
}
.wf-flow__edge-label {
  font-size: 10.5px;
  fill: #64748b;
  paint-order: stroke;
  stroke: #fafafa;
  stroke-width: 3px;
}
</style>
